// Disease and Pest Database
// Comprehensive guide to garden problems and solutions

export interface PestDisease {
  id: string
  name: string
  type: 'pest' | 'disease' | 'fungus' | 'deficiency'
  affectedPlants: string[] // Plant IDs or categories
  symptoms: string[]
  causes?: string[]
  prevention: string[]
  treatments: {
    natural: TreatmentOption[]
    chemical: TreatmentOption[]
  }
  severity: 'low' | 'medium' | 'high' | 'critical'
  seasonality: string[]
}

export interface TreatmentOption {
  name: string
  description: string
  instructions: string
  frequency?: string
  effectiveness: 'low' | 'medium' | 'high'
  cost: '$' | '$$' | '$$$'
  safetyNotes?: string
}

// Common Garden Pests (original 8)
const originalPests: PestDisease[] = [
  {
    id: 'pest-aphids',
    name: 'Aphids',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'lettuce', 'kale', 'cabbage', 'broccoli', 'rose', 'bean'],
    symptoms: [
      'Clusters of small green, black, or white insects on stems and undersides of leaves',
      'Leaves curling, yellowing, or distorted',
      'Sticky honeydew on leaves',
      'Sooty mold growing on honeydew',
      'Stunted plant growth'
    ],
    causes: ['Over-fertilization with nitrogen', 'Lack of beneficial insects', 'Dense plantings with poor air circulation'],
    prevention: [
      'Attract beneficial insects like ladybugs and lacewings with companion flowers',
      'Avoid over-fertilizing with nitrogen',
      'Plant resistant varieties',
      'Use reflective mulches to confuse aphids',
      'Maintain proper spacing for air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Strong Water Spray',
          description: 'Blast aphids off plants with strong water jet',
          instructions: 'Spray plants daily with strong water stream, focusing on undersides of leaves. Repeat for 3-5 days.',
          frequency: 'Daily for 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil Spray',
          description: 'Organic oil that disrupts aphid feeding and reproduction',
          instructions: 'Mix 1-2 tbsp neem oil with 1 gallon water and few drops dish soap. Spray all plant surfaces. Apply in evening.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Do not apply in direct sun. Beneficial insects may be affected.'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Potassium salts of fatty acids that kill soft-bodied insects',
          instructions: 'Mix 2.5 tbsp per gallon water. Spray directly on aphids. Must contact insects to work.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Ladybug Release',
          description: 'Beneficial insects that eat aphids',
          instructions: 'Release 1,500 ladybugs per 100 sq ft in evening. Mist plants first so ladybugs drink before eating.',
          frequency: 'One-time release',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Garlic Spray',
          description: 'Homemade repellent spray',
          instructions: 'Blend 2 bulbs garlic with 1 quart water. Strain, add 1 tbsp dish soap. Spray on plants.',
          frequency: 'Every 3-5 days',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin Spray',
          description: 'Natural insecticide derived from chrysanthemums',
          instructions: 'Mix according to label. Spray to wet all surfaces. Kills on contact.',
          frequency: 'Every 7-14 days as needed',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees and beneficial insects. Apply in evening when bees are inactive.'
        },
        {
          name: 'Malathion',
          description: 'Broad-spectrum insecticide',
          instructions: 'Mix 1-2 tbsp per gallon water. Spray thoroughly.',
          frequency: 'Every 7 days, max 3 applications',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Wait 7 days before harvest. Toxic to bees and aquatic life.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-tomato-hornworm',
    name: 'Tomato Hornworm',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'potato'],
    symptoms: [
      'Large green caterpillars (3-4 inches) with white V-shaped markings',
      'Leaves chewed from edges inward',
      'Stems stripped of leaves',
      'Black or green droppings (frass) on leaves and ground',
      'Fruit damage with holes'
    ],
    causes: ['Moths laying eggs on plants', 'Lack of natural predators'],
    prevention: [
      'Till soil in fall and spring to destroy pupae',
      'Plant dill, parsley, and flowers to attract parasitic wasps',
      'Inspect plants daily and handpick eggs',
      'Use row covers early in season'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Most effective method for small gardens',
          instructions: 'Check plants daily, especially undersides of leaves. Drop caterpillars into soapy water or feed to chickens.',
          frequency: 'Daily inspection',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt (Bacillus thuringiensis)',
          description: 'Natural bacteria that kills caterpillars',
          instructions: 'Mix 1-2 tsp per gallon water. Spray on leaves where caterpillars feed. They stop eating within hours, die in 2-3 days.',
          frequency: 'Every 7-10 days or after rain',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Safe for beneficial insects, bees, and humans. Organic approved.'
        },
        {
          name: 'Parasitic Wasps',
          description: 'Braconid wasps lay eggs inside hornworms',
          instructions: 'Plant dill, parsley, and flowers to attract wasps. If you see hornworms with white cocoons on their backs, leave them - the wasps are working!',
          frequency: 'Ongoing habitat building',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Sevin (Carbaryl)',
          description: 'Broad-spectrum insecticide',
          instructions: 'Dust or spray according to label. Apply in evening.',
          frequency: 'Every 7-10 days as needed',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Toxic to bees. Wait 3-7 days before harvest depending on crop.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-cabbage-worm',
    name: 'Cabbage Worm / Cabbage Looper',
    type: 'pest',
    affectedPlants: ['cabbage', 'broccoli', 'cauliflower', 'kale', 'collard', 'brussels sprout'],
    symptoms: [
      'Small green caterpillars on undersides of leaves',
      'Holes chewed in leaves, often between veins',
      'Green droppings on leaves',
      'Stunted head formation in cabbage',
      'White butterflies (adults) flying around plants'
    ],
    causes: ['White cabbage butterflies laying eggs', 'Imported from Europe'],
    prevention: [
      'Use row covers to exclude butterflies',
      'Plant red/purple varieties which are less attractive',
      'Interplant with herbs like thyme and rosemary',
      'Attract parasitic wasps with flowers'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Physical barrier prevents butterflies from laying eggs',
          instructions: 'Cover plants with lightweight floating row cover immediately after planting. Secure edges with soil or pins.',
          frequency: 'Keep covered until harvest',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Hand Picking',
          description: 'Check leaves daily and remove caterpillars',
          instructions: 'Turn over leaves and look for green caterpillars and yellow eggs. Crush eggs, drop caterpillars in soapy water.',
          frequency: 'Daily',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Bacillus thuringiensis kills caterpillars',
          instructions: 'Spray on leaves where caterpillars feed. Must be eaten to work.',
          frequency: 'Every 7-10 days or after rain',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Natural substance from soil bacteria',
          instructions: 'Mix according to label. Spray on foliage. Kills caterpillars within 1-2 days.',
          frequency: 'Every 5-7 days as needed',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees when wet. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-squash-vine-borer',
    name: 'Squash Vine Borer',
    type: 'pest',
    affectedPlants: ['squash', 'zucchini', 'pumpkin', 'gourd'],
    symptoms: [
      'Sudden wilting of plant despite adequate water',
      'Small holes at base of stem with sawdust-like frass',
      'Stem becomes mushy at base',
      'Plant collapses and dies',
      'Adult moth resembles red and black wasp'
    ],
    causes: ['Moths laying eggs at base of stems', 'Larvae bore into stems and feed inside'],
    prevention: [
      'Wrap stem base with aluminum foil or row cover material',
      'Plant resistant varieties (butternut, tromboncino)',
      'Use floating row covers until flowering',
      'Plant early to harvest before borers peak (July)',
      'Rotate crops - don\'t plant squash in same spot for 3 years'
    ],
    treatments: {
      natural: [
        {
          name: 'Stem Wrapping',
          description: 'Prevent moths from laying eggs at stem base',
          instructions: 'Wrap aluminum foil or nylon stocking around stem base from soil line to 2 inches up. Secure loosely as stem grows.',
          frequency: 'Apply when plants have 3-4 leaves',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Surgical Removal',
          description: 'Cut out borer if detected early',
          instructions: 'Slit stem lengthwise at entry hole with sharp knife. Remove larva (white with brown head). Mound soil over wound to encourage rooting.',
          frequency: 'As soon as wilting is noticed',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Row Covers',
          description: 'Block moths from reaching plants',
          instructions: 'Cover plants with floating row cover until female flowers appear. Must remove for pollination.',
          frequency: 'Until flowering begins',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Trap Crops',
          description: 'Plant sacrificial squash to attract borers',
          instructions: 'Plant hubbard squash 2 weeks before main crop. Destroy trap crop when infested.',
          frequency: 'Plant 2 weeks early',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Permethrin Spray',
          description: 'Synthetic insecticide for stem base',
          instructions: 'Spray lower stems and soil around base every 5-7 days starting when vines begin to run.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest. Toxic to bees and fish.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-japanese-beetle',
    name: 'Japanese Beetle',
    type: 'pest',
    affectedPlants: ['rose', 'bean', 'grape', 'raspberry', 'blueberry', 'corn', 'tomato'],
    symptoms: [
      'Metallic green and copper beetles feeding on leaves',
      'Leaves skeletonized (only veins remain)',
      'Flowers and fruit damaged',
      'Beetles cluster on plants in morning and evening'
    ],
    causes: ['Grubs in soil mature into beetles', 'Attracted to over-ripe fruit and certain plants'],
    prevention: [
      'Apply milky spore disease to lawn (long-term grub control)',
      'Plant geraniums nearby - beetles eat them and die',
      'Choose resistant plants (boxwood, dogwood, forsythia)',
      'Avoid Japanese beetle favorites (roses, grapes, linden)'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking + Soapy Water',
          description: 'Most effective for small infestations',
          instructions: 'Shake beetles into bucket of soapy water in early morning when sluggish. Repeat daily.',
          frequency: 'Daily during peak (July)',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Pheromone Traps',
          description: 'Attracts and traps beetles',
          instructions: 'Place traps 30 feet AWAY from garden to draw beetles away. Empty frequently.',
          frequency: 'Check and empty daily',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'DO NOT place in garden - will attract more beetles to your plants!'
        },
        {
          name: 'Neem Oil Spray',
          description: 'Repels beetles and stops feeding',
          instructions: 'Spray on plants. Acts as repellent and reduces feeding.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Geranium Flowers',
          description: 'Natural beetle killer',
          instructions: 'Plant geraniums near vulnerable crops. Beetles eat geraniums, become paralyzed, and die.',
          frequency: 'Plant before beetle season',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Kills beetles on contact',
          instructions: 'Dust or spray on plants. Kills quickly.',
          frequency: 'Every 7-10 days as needed',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Highly toxic to bees. Apply in evening. Wait 7-14 days before harvest.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-slugs-snails',
    name: 'Slugs and Snails',
    type: 'pest',
    affectedPlants: ['lettuce', 'spinach', 'kale', 'cabbage', 'strawberry', 'tomato', 'pepper'],
    symptoms: [
      'Large irregular holes in leaves, often with smooth edges',
      'Silvery slime trails on leaves and soil',
      'Young seedlings completely consumed',
      'Damage worst in damp weather and at night'
    ],
    causes: ['Moist conditions', 'Mulch providing hiding places', 'Mild temperatures (60-70°F)'],
    prevention: [
      'Water in morning so soil dries by evening',
      'Use drip irrigation instead of overhead watering',
      'Remove hiding places - boards, debris, dense weeds',
      'Copper tape around raised beds',
      'Diatomaceous earth around plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking at Night',
          description: 'Most effective control method',
          instructions: 'Go out 2 hours after dark with flashlight. Pick slugs and drop in soapy water or salt. Repeat nightly for 1-2 weeks.',
          frequency: 'Nightly for 1-2 weeks',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Beer Traps',
          description: 'Attracts and drowns slugs',
          instructions: 'Bury shallow containers (tuna cans) so rim is at soil level. Fill with beer. Empty and refill every 2-3 days.',
          frequency: 'Check every 2-3 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Iron Phosphate Bait',
          description: 'Safe, organic slug killer',
          instructions: 'Scatter pellets around plants in evening. Slugs eat bait and stop feeding, die in 3-6 days.',
          frequency: 'Every 2 weeks or after heavy rain',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Safe for pets, wildlife, and humans. Organic approved.'
        },
        {
          name: 'Copper Tape/Wire',
          description: 'Barrier slugs won\'t cross',
          instructions: 'Apply copper tape around pots or raised bed edges. Must be clean - oxidation reduces effectiveness.',
          frequency: 'One-time application',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Abrasive powder damages slug bodies',
          instructions: 'Dust around plant bases. Reapply after rain or watering. Must be dry to work.',
          frequency: 'After rain or watering',
          effectiveness: 'medium',
          cost: '$',
          safetyNotes: 'Use food-grade DE. Wear mask when applying.'
        }
      ],
      chemical: [
        {
          name: 'Metaldehyde Bait',
          description: 'Traditional slug killer - toxic to pets',
          instructions: 'Scatter pellets in evening. Keep pets and children away.',
          frequency: 'Every 2 weeks',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'TOXIC to dogs and cats. Use iron phosphate instead if pets present.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-spider-mites',
    name: 'Spider Mites',
    type: 'pest',
    affectedPlants: ['tomato', 'bean', 'cucumber', 'squash', 'strawberry', 'rose'],
    symptoms: [
      'Tiny yellow or white spots on leaves (stippling)',
      'Fine webbing on undersides of leaves',
      'Leaves turning bronze or yellow',
      'Leaves drying up and falling off',
      'Tiny moving dots on leaves (use magnifying glass)'
    ],
    causes: ['Hot, dry conditions', 'Drought stress', 'Indoor growing conditions'],
    prevention: [
      'Maintain adequate humidity around plants',
      'Water regularly to prevent drought stress',
      'Spray plants with water to increase humidity',
      'Avoid over-fertilizing with nitrogen'
    ],
    treatments: {
      natural: [
        {
          name: 'Strong Water Spray',
          description: 'Blast mites off plants',
          instructions: 'Spray undersides of leaves with strong water jet daily for 3-5 days. Mites dislike humidity and are washed away.',
          frequency: 'Daily for 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills mites on contact',
          instructions: 'Spray thoroughly, especially undersides of leaves. Must contact mites to kill.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Disrupts mite feeding and reproduction',
          instructions: 'Mix 1-2 tbsp per gallon water. Spray all surfaces. Apply in evening.',
          frequency: 'Every 7 days',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Predatory Mites',
          description: 'Beneficial mites that eat pest mites',
          instructions: 'Release predatory mites (Phytoseiulus persimilis) according to package instructions.',
          frequency: 'One-time release',
          effectiveness: 'high',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Miticide (Avid, Floramite)',
          description: 'Chemical specifically for mites',
          instructions: 'Follow label carefully. Rotate with different chemical classes to prevent resistance.',
          frequency: 'Every 7-14 days',
          effectiveness: 'high',
          cost: '$$$',
          safetyNotes: 'Many miticides are toxic to bees. Apply carefully.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-cutworms',
    name: 'Cutworms',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'cabbage', 'broccoli', 'bean', 'corn'],
    symptoms: [
      'Seedlings cut off at soil line',
      'Plants falling over with no roots attached',
      'Small holes in stems at soil level',
      'Cutworm curled in soil near damaged plant (gray/brown caterpillar)'
    ],
    causes: ['Larvae of night-flying moths', 'Weedy garden beds', 'Cool spring weather'],
    prevention: [
      'Use cardboard or metal collars around seedlings',
      'Remove weeds 2 weeks before planting',
      'Till soil 2 weeks before planting to expose larvae to birds',
      'Plant seedlings when soil is warmer (cutworms less active)',
      'Use toothpicks inserted next to stem to prevent cutting'
    ],
    treatments: {
      natural: [
        {
          name: 'Cardboard Collars',
          description: 'Physical barrier prevents cutting',
          instructions: 'Wrap cardboard around stem, pushing 1 inch into soil and extending 2 inches above. Remove when stem hardens.',
          frequency: 'At planting time',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Toothpick Trick',
          description: 'Prevents cutworm from wrapping around stem',
          instructions: 'Insert toothpick or small stick vertically into soil right next to seedling stem. Cutworm can\'t bend stem around obstacle.',
          frequency: 'At planting time',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Hand Picking',
          description: 'Find and destroy cutworms',
          instructions: 'Dig around damaged plants 1-2 inches deep. Find curled cutworms and destroy. Check in evening with flashlight.',
          frequency: 'Daily until problem stops',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Beneficial Nematodes',
          description: 'Microscopic worms that kill cutworm larvae',
          instructions: 'Apply to soil according to package instructions. Water in well. Effective in warm soil (above 55°F).',
          frequency: 'Spring application',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Bifenthrin Granules',
          description: 'Soil insecticide for cutworms',
          instructions: 'Apply granules to soil surface and water in before planting.',
          frequency: 'At planting time',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees and aquatic life. Use collars instead when possible.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  }
]

// Common Plant Diseases (original 6)
const originalDiseases: PestDisease[] = [
  {
    id: 'disease-early-blight',
    name: 'Early Blight',
    type: 'disease',
    affectedPlants: ['tomato', 'potato', 'eggplant', 'pepper'],
    symptoms: [
      'Dark brown to black spots on lower leaves',
      'Spots have concentric rings (target-like appearance)',
      'Yellowing around spots',
      'Leaves turn brown and drop from bottom up',
      'Stem lesions near soil line'
    ],
    causes: ['Fungus Alternaria solani', 'Wet foliage', 'Warm humid weather', 'Old plant debris in soil'],
    prevention: [
      'Rotate crops - don\'t plant tomatoes/potatoes in same spot for 3+ years',
      'Water at base of plants, avoid wetting foliage',
      'Space plants for good air circulation',
      'Remove and destroy infected lower leaves',
      'Mulch to prevent soil splash',
      'Use resistant varieties'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Leaves',
          description: 'Prune bottom leaves to improve airflow',
          instructions: 'Remove yellowing and spotted lower leaves, especially those touching ground. Dispose in trash, not compost. Sterilize pruners between cuts.',
          frequency: 'Weekly inspection and removal',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Organic-approved fungicide',
          instructions: 'Spray on foliage according to label. Best used as preventive or at first sign of disease.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Organic approved but use sparingly - copper accumulates in soil.'
        },
        {
          name: 'Baking Soda Spray',
          description: 'Homemade fungicide',
          instructions: 'Mix 1 tbsp baking soda + 1 tsp dish soap + 1 gallon water. Spray on foliage.',
          frequency: 'Every 5-7 days',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Serenade (Bacillus subtilis)',
          description: 'Biological fungicide',
          instructions: 'Spray on plants. Beneficial bacteria outcompete disease organisms.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Broad-spectrum fungicide',
          instructions: 'Spray at first sign of disease and every 7-10 days.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest. Protective clothing recommended.'
        },
        {
          name: 'Mancozeb',
          description: 'Protectant fungicide',
          instructions: 'Apply before disease appears or at first sign. Must cover all surfaces.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Wait 5 days before harvest.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'disease-late-blight',
    name: 'Late Blight',
    type: 'disease',
    affectedPlants: ['tomato', 'potato'],
    symptoms: [
      'Large dark brown/black lesions on leaves',
      'White fuzzy growth on undersides of leaves (in humid conditions)',
      'Brown lesions on stems',
      'Firm brown spots on fruit',
      'Rapid plant collapse in cool, wet weather',
      'Foul odor from rotting tissue'
    ],
    causes: ['Phytophthora infestans (same fungus that caused Irish Potato Famine)', 'Cool wet weather', 'Spores spread by wind'],
    prevention: [
      'Plant resistant varieties',
      'Destroy volunteer potatoes and tomatoes',
      'Avoid overhead watering',
      'Space plants for air circulation',
      'Remove and destroy infected plants immediately',
      'Apply preventive fungicide before disease appears in wet seasons'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove and Destroy Infected Plants',
          description: 'Critical - late blight spreads rapidly',
          instructions: 'Pull entire infected plant immediately. Put in plastic bag and trash - do NOT compost. Check neighboring plants daily.',
          frequency: 'Immediately upon detection',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Copper Fungicide Preventive Spray',
          description: 'Must be applied before infection',
          instructions: 'Spray before cool wet weather arrives. Will not cure infected plants.',
          frequency: 'Every 5-7 days during wet weather',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil + Metalaxyl',
          description: 'Systemic fungicide for late blight',
          instructions: 'Apply at first sign of disease. Must rotate with different chemistry to prevent resistance.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$$$',
          safetyNotes: 'Wait 7 days before harvest. Late blight can develop resistance - rotate fungicides.'
        }
      ]
    },
    severity: 'critical',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'disease-powdery-mildew',
    name: 'Powdery Mildew',
    type: 'fungus',
    affectedPlants: ['squash', 'cucumber', 'melon', 'zucchini', 'pumpkin', 'grape', 'rose'],
    symptoms: [
      'White powdery spots on leaves and stems',
      'Leaves turn yellow and dry out',
      'Distorted or stunted growth',
      'White coating on fruit',
      'Unlike downy mildew, NOT limited to lower leaves'
    ],
    causes: ['Various fungal species', 'High humidity (but not wet leaves)', 'Poor air circulation', 'Crowded plantings'],
    prevention: [
      'Plant resistant varieties',
      'Space plants for good air flow',
      'Avoid overhead watering',
      'Remove and destroy infected leaves',
      'Apply preventive sprays before symptoms appear',
      'Grow in full sun'
    ],
    treatments: {
      natural: [
        {
          name: 'Milk Spray',
          description: 'Surprisingly effective organic treatment',
          instructions: 'Mix 1 part milk with 9 parts water. Spray on affected plants in morning sun. The proteins interact with sunlight to kill fungus.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Baking Soda Spray',
          description: 'Changes pH to inhibit fungus',
          instructions: 'Mix 1 tbsp baking soda + 1/2 tsp liquid soap + 1 gallon water. Spray on foliage.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Fungicide and insecticide',
          instructions: 'Mix 1-2 tbsp per gallon water. Spray all surfaces. Apply in evening.',
          frequency: 'Every 7-14 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Remove Infected Leaves',
          description: 'Reduce spread by pruning',
          instructions: 'Remove worst-affected leaves and dispose in trash. Don\'t remove more than 1/3 of plant.',
          frequency: 'As needed',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Serenade (Bacillus subtilis)',
          description: 'Biological fungicide',
          instructions: 'Spray preventively or at first sign. Beneficial bacteria compete with fungus.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Myclobutanil',
          description: 'Systemic fungicide',
          instructions: 'Spray at first sign and every 7-14 days. Absorbed by plant for continued protection.',
          frequency: 'Every 7-14 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 7 days before harvest on vegetables.'
        },
        {
          name: 'Propiconazole',
          description: 'Systemic fungicide',
          instructions: 'Apply at first sign of disease. Provides 7-14 days protection.',
          frequency: 'Every 7-14 days',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'disease-blossom-end-rot',
    name: 'Blossom End Rot',
    type: 'deficiency',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'squash', 'watermelon'],
    symptoms: [
      'Dark sunken lesion on blossom end (bottom) of fruit',
      'Lesion starts as water-soaked spot and enlarges',
      'Not a disease - caused by calcium deficiency in fruit',
      'Affects green and ripening fruit',
      'New growth may appear normal'
    ],
    causes: ['Calcium deficiency in developing fruit', 'Inconsistent watering', 'Root damage', 'High nitrogen fertilization', 'Rapid plant growth'],
    prevention: [
      'Maintain consistent soil moisture - mulching helps',
      'Add calcium to soil before planting (lime, gypsum, crushed eggshells)',
      'Avoid over-fertilizing with nitrogen',
      'Protect roots from cultivation damage',
      'Foliar calcium sprays during fruit development'
    ],
    treatments: {
      natural: [
        {
          name: 'Consistent Watering',
          description: 'Most important factor',
          instructions: 'Water deeply and regularly. Use mulch to maintain even soil moisture. Never let soil completely dry out.',
          frequency: 'Ongoing',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Calcium Foliar Spray',
          description: 'Quick fix for current crop',
          instructions: 'Mix 2-3 tbsp calcium chloride or calcium nitrate per gallon water. Spray on leaves and fruit every 5-7 days.',
          frequency: 'Every 5-7 days during fruiting',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Crushed Eggshells',
          description: 'Slow-release calcium for soil',
          instructions: 'Work crushed eggshells into soil around plants. Takes time to break down but provides long-term calcium.',
          frequency: 'Apply at planting and mid-season',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Epsom Salt Spray',
          description: 'Magnesium helps calcium uptake',
          instructions: 'Mix 1 tbsp Epsom salt per gallon water. Spray on foliage. Helps plant use calcium more efficiently.',
          frequency: 'Every 2 weeks',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Calcium Nitrate Foliar Feed',
          description: 'Fast-acting calcium supplement',
          instructions: 'Dissolve 2-4 tbsp per gallon water. Spray on leaves and fruit.',
          frequency: 'Every 7 days during fruit development',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'disease-root-knot-nematode',
    name: 'Root Knot Nematodes',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'cucumber', 'squash', 'bean', 'carrot'],
    symptoms: [
      'Stunted, yellowing plants despite adequate water and fertilizer',
      'Plants wilt during heat of day',
      'Knobby, swollen galls on roots when dug up',
      'Roots appear "hairy" with many small side roots',
      'Plants decline over season'
    ],
    causes: ['Microscopic soil-dwelling worms (Meloidogyne species)', 'Spread by contaminated soil, tools, or plants', 'Thrive in sandy soils and warm climates'],
    prevention: [
      'Plant resistant varieties (look for "N" in tomato variety codes)',
      'Solarize soil in summer to kill nematodes',
      'Add organic matter - compost and manures',
      'Rotate with nematode-resistant crops (marigolds, asparagus)',
      'Plant French marigolds as cover crop'
    ],
    treatments: {
      natural: [
        {
          name: 'French Marigold Cover Crop',
          description: 'Natural nematode suppressant',
          instructions: 'Plant French marigolds (Tagetes patula) thickly in infested area. Grow for 2-3 months, then till into soil.',
          frequency: 'Plant spring and/or fall',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Soil Solarization',
          description: 'Use sun to kill nematodes',
          instructions: 'Wet soil thoroughly, cover with clear plastic, seal edges. Leave for 4-6 weeks in summer.',
          frequency: 'Once per year in summer',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Add Organic Matter',
          description: 'Compost and manures help suppress nematodes',
          instructions: 'Add 2-4 inches compost or well-rotted manure to soil. Beneficial organisms in compost compete with nematodes.',
          frequency: '2-3 times per year',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Beneficial Nematodes',
          description: 'Good nematodes that eat bad nematodes',
          instructions: 'Apply Steinernema or Heterorhabditis species according to package. Water in well.',
          frequency: 'Spring and fall applications',
          effectiveness: 'medium',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Nematicides',
          description: 'Soil fumigants for severe infestations',
          instructions: 'Professional application usually required. Toxic to humans and environment.',
          frequency: 'Pre-planting treatment',
          effectiveness: 'high',
          cost: '$$$',
          safetyNotes: 'Highly toxic. Restricted use in many areas. Consider organic methods first.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['year-round']
  }
]

// Helper functions
// =====================================================
// ADDITIONAL PESTS
// =====================================================

export const additionalPests: PestDisease[] = [
  {
    id: 'pest-flea-beetle',
    name: 'Flea Beetle',
    type: 'pest',
    affectedPlants: ['eggplant', 'potato', 'tomato', 'pepper', 'cabbage', 'broccoli', 'cauliflower', 'kale', 'radish', 'turnip'],
    symptoms: [
      'Numerous tiny round holes in leaves (shot hole appearance)',
      'Leaves look peppered or stippled',
      'Seedlings may be killed or stunted',
      'Small black or brown beetles that jump when disturbed',
      'Most damage in spring on young plants'
    ],
    causes: ['Overwintered adults emerge in spring', 'Weedy areas nearby', 'Lack of beneficial insects'],
    prevention: [
      'Use row covers on young transplants',
      'Plant trap crops (radish, mustard) to draw beetles away',
      'Keep garden free of weeds',
      'Delay planting until weather warms',
      'Use reflective mulch to confuse beetles'
    ],
    treatments: {
      natural: [
        {
          name: 'Diatomaceous Earth',
          description: 'Abrasive powder damages beetle exoskeletons',
          instructions: 'Dust plants thoroughly, especially undersides of leaves. Reapply after rain.',
          frequency: 'Every 3-5 days or after rain',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil Spray',
          description: 'Repels beetles and disrupts feeding',
          instructions: 'Mix 1-2 tbsp neem oil per gallon water. Spray all leaf surfaces.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Sticky Traps',
          description: 'Monitor and catch adult beetles',
          instructions: 'Place white or yellow sticky traps near plants. Check daily.',
          frequency: 'Replace when full',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin Spray',
          description: 'Kills beetles on contact',
          instructions: 'Spray directly on beetles. Kills quickly but has no residual effect.',
          frequency: 'Every 5-7 days as needed',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-colorado-potato-beetle',
    name: 'Colorado Potato Beetle',
    type: 'pest',
    affectedPlants: ['potato', 'tomato', 'eggplant', 'pepper'],
    symptoms: [
      'Adults: Yellow-orange beetles with black stripes',
      'Larvae: Reddish-orange grubs with black spots',
      'Leaves chewed from edges inward',
      'Complete defoliation if not controlled',
      'Eggs: Orange clusters on undersides of leaves'
    ],
    causes: ['Overwintering adults in soil', 'Continuous planting of solanaceous crops', 'Lack of crop rotation'],
    prevention: [
      'Rotate crops - don\'t plant potatoes/tomatoes in same spot for 3+ years',
      'Use floating row covers',
      'Plant early varieties to harvest before peak beetle populations',
      'Remove and destroy egg masses daily',
      'Use deep straw mulch to interfere with beetle movement'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Most effective for small gardens',
          instructions: 'Check undersides of leaves for orange egg clusters and crush. Pick adults and larvae into soapy water.',
          frequency: 'Daily during infestation',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt San Diego',
          description: 'Specific Bt strain for potato beetle',
          instructions: 'Spray on leaves. Must be eaten by larvae. Effective only on young larvae.',
          frequency: 'Every 3-5 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Neem Oil',
          description: 'Repels adults and kills young larvae',
          instructions: 'Spray thoroughly on all leaf surfaces. Apply in evening.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Natural soil bacteria derivative',
          instructions: 'Spray on foliage. Kills larvae within 1-2 days.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees when wet. Apply in evening.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-mexican-bean-beetle',
    name: 'Mexican Bean Beetle',
    type: 'pest',
    affectedPlants: ['bean', 'lima bean', 'soybean', 'cowpea'],
    symptoms: [
      'Adults: Copper-colored beetles with 16 black spots',
      'Larvae: Yellow, fuzzy/grub-like, spiny',
      'Leaves skeletonized (only veins remain)',
      'Damage starts on undersides of leaves',
      'Eggs: Yellow clusters on undersides of leaves'
    ],
    causes: ['Overwintering adults in garden debris', 'Late planted beans', 'Lack of natural predators'],
    prevention: [
      'Plant beans early to mature before peak beetle populations',
      'Use floating row covers until flowering',
      'Remove and destroy egg clusters daily',
      'Plant resistant varieties',
      'Attract parasitic wasps with flowers'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Effective for small plantings',
          instructions: 'Look for yellow egg clusters on undersides of leaves and crush. Pick adults and larvae into soapy water.',
          frequency: 'Daily',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills larvae on contact',
          instructions: 'Spray undersides of leaves thoroughly. Must contact insects.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Repels adults, kills larvae',
          instructions: 'Spray all leaf surfaces, especially undersides.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin',
          description: 'Kills on contact',
          instructions: 'Spray directly on beetles and larvae.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-thrips',
    name: 'Thrips',
    type: 'pest',
    affectedPlants: ['onion', 'garlic', 'rose', 'tomato', 'pepper', 'cucumber', 'squash', 'bean'],
    symptoms: [
      'Silvery or bronze streaks on leaves',
      'Leaves may curl or distort',
      'Black specks (frass) on leaves',
      'Flowers discolored or deformed',
      'Tiny, slender insects (1/16 inch) inside flowers and on leaves'
    ],
    causes: ['Hot, dry weather', 'Weedy areas nearby', 'Overhead irrigation'],
    prevention: [
      'Use reflective mulch to confuse thrips',
      'Keep garden weed-free',
      'Avoid excessive nitrogen fertilizer',
      'Plant resistant varieties',
      'Maintain adequate soil moisture'
    ],
    treatments: {
      natural: [
        {
          name: 'Insecticidal Soap',
          description: 'Kills thrips on contact',
          instructions: 'Spray thoroughly, especially on undersides of leaves and in flowers.',
          frequency: 'Every 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Disrupts feeding and reproduction',
          instructions: 'Mix 1-2 tbsp per gallon water. Spray all plant surfaces.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Blue Sticky Traps',
          description: 'Attracts and catches thrips',
          instructions: 'Place blue sticky cards near plants. Thrips are attracted to blue.',
          frequency: 'Replace when full',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Effective against thrips',
          instructions: 'Spray on foliage and flowers.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees when wet. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'pest-whitefly',
    name: 'Whitefly',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'cucumber', 'squash', 'bean', 'cabbage', 'broccoli'],
    symptoms: [
      'Tiny white insects fly up when plant is disturbed',
      'Leaves yellow and drop',
      'Sticky honeydew on leaves',
      'Sooty mold growing on honeydew',
      'Stunted plant growth'
    ],
    causes: ['Warm temperatures', 'Indoor/greenhouse growing', 'Lack of natural enemies'],
    prevention: [
      'Use yellow sticky traps to monitor and catch adults',
      'Attract beneficial insects with flowers',
      'Avoid over-fertilizing with nitrogen',
      'Use reflective mulch',
      'Inspect new plants before bringing into garden'
    ],
    treatments: {
      natural: [
        {
          name: 'Yellow Sticky Traps',
          description: 'Catches adult whiteflies',
          instructions: 'Place traps near plant tops. Whiteflies attracted to yellow.',
          frequency: 'Replace when full',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills nymphs on contact',
          instructions: 'Spray undersides of leaves thoroughly where nymphs feed.',
          frequency: 'Every 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Kills nymphs and repels adults',
          instructions: 'Spray all plant surfaces, especially undersides of leaves.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Vacuuming',
          description: 'Remove adults with handheld vacuum',
          instructions: 'Use handheld vacuum in early morning when whiteflies are sluggish. Empty into soapy water.',
          frequency: 'Every few days',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Insecticidal Soap + Pyrethrin',
          description: 'Combination for better control',
          instructions: 'Spray on foliage, especially undersides.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'pest-corn-earworm',
    name: 'Corn Earworm (Tomato Fruitworm)',
    type: 'pest',
    affectedPlants: ['corn', 'tomato', 'pepper', 'eggplant', 'bean'],
    symptoms: [
      'Corn: Worms feeding on silks and kernels at tip of ear',
      'Tomatoes: Holes in fruit with frass visible',
      'Peppers: Holes in fruit, seeds eaten',
      'Green, brown, or pink caterpillars (1-2 inches)',
      'Eggs: Tiny white spheres on corn silks or tomato leaves'
    ],
    causes: ['Moths fly in from southern overwintering areas', 'Late planted corn', 'Lack of beneficial insects'],
    prevention: [
      'Plant early corn varieties',
      'Use corn varieties with tight husks',
      'Apply mineral oil to corn silks (5 drops per ear)',
      'Encourage beneficial wasps with flowers',
      'Remove and destroy infested fruit'
    ],
    treatments: {
      natural: [
        {
          name: 'Mineral Oil on Corn Silks',
          description: 'Prevents larvae from entering ear',
          instructions: 'Apply 5 drops of mineral oil to each ear of corn silk 3-7 days after silks appear.',
          frequency: 'Once per ear',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Kills young caterpillars',
          instructions: 'Spray on corn silks and tomato fruit. Must be eaten to work.',
          frequency: 'Every 3-5 days during silking/fruiting',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Hand Picking',
          description: 'Remove worms from fruit',
          instructions: 'Check fruit daily and remove worms. Destroy infested fruit.',
          frequency: 'Daily during fruiting',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Effective on caterpillars',
          instructions: 'Spray on silks and fruit.',
          frequency: 'Every 3-5 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest on tomatoes.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-squash-bug',
    name: 'Squash Bug',
    type: 'pest',
    affectedPlants: ['squash', 'zucchini', 'pumpkin', 'cucumber', 'melon'],
    symptoms: [
      'Gray or brown bugs (5/8 inch) on leaves and stems',
      'Yellow spots on leaves that turn brown',
      'Wilting of leaves and vines',
      'Eggs: Bronze, football-shaped clusters on undersides of leaves',
      'Young nymphs: Gray with black legs, found in groups'
    ],
    causes: ['Overwintering adults in garden debris', 'Continuous cucurbit planting', 'Lack of crop rotation'],
    prevention: [
      'Rotate crops - don\'t plant cucurbits in same spot for 2+ years',
      'Use floating row covers until flowering',
      'Plant resistant varieties (butternut, royal acorn)',
      'Remove plant debris in fall to eliminate overwintering sites',
      'Place boards on ground near plants - bugs hide underneath during day'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking & Egg Removal',
          description: 'Most effective control method',
          instructions: 'Check undersides of leaves for bronze egg clusters and destroy. Pick adults and nymphs into soapy water. Look under boards placed near plants.',
          frequency: 'Daily during infestation',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Kills nymphs',
          instructions: 'Dust plants, especially base of stems. Reapply after rain.',
          frequency: 'Every 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills young nymphs',
          instructions: 'Spray directly on nymphs. Less effective on adults.',
          frequency: 'Every 3-5 days',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Permethrin',
          description: 'Controls squash bugs',
          instructions: 'Spray on plants, especially base of stems.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest. Toxic to bees and fish.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-cucumber-beetle',
    name: 'Cucumber Beetle',
    type: 'pest',
    affectedPlants: ['cucumber', 'squash', 'zucchini', 'pumpkin', 'melon', 'watermelon', 'bean', 'corn'],
    symptoms: [
      'Striped: Yellow with black stripes (western and eastern types)',
      'Spotted: Yellow with black spots',
      'Holes in leaves, flowers, and fruit',
      'Seedlings may be killed or stunted',
      'Transmits bacterial wilt disease'
    ],
    causes: ['Overwintering adults', 'Weedy areas with wild cucurbits', 'Warm spring weather'],
    prevention: [
      'Use floating row covers until flowering',
      'Plant resistant varieties',
      'Delay planting until weather warms (beetles emerge early)',
      'Remove weeds and wild cucurbits near garden',
      'Apply kaolin clay to deter feeding'
    ],
    treatments: {
      natural: [
        {
          name: 'Kaolin Clay (Surround)',
          description: 'White clay film deters beetles',
          instructions: 'Mix with water and spray on plants. Forms white film that beetles avoid.',
          frequency: 'Every 7 days or after rain',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Row Covers',
          description: 'Physical barrier',
          instructions: 'Cover plants with floating row cover until flowering. Must remove for pollination.',
          frequency: 'Until flowering',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Yellow Sticky Traps',
          description: 'Monitor and catch adults',
          instructions: 'Place traps near plants. Check daily.',
          frequency: 'Replace when full',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Kills beetles',
          instructions: 'Dust or spray on plants.',
          frequency: 'Every 5-7 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Highly toxic to bees. Apply in evening. Wait 3 days before harvest.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer']
  }
]

// =====================================================
// ADDITIONAL DISEASES
// =====================================================

export const additionalDiseases: PestDisease[] = [
  {
    id: 'disease-septoria-leaf-spot',
    name: 'Septoria Leaf Spot',
    type: 'disease',
    affectedPlants: ['tomato', 'eggplant'],
    symptoms: [
      'Small circular spots on lower leaves (1/16 to 1/8 inch)',
      'Spots have dark brown borders with gray or tan centers',
      'Tiny black dots (fungal fruiting bodies) in center of spots',
      'Yellowing of leaves starting from bottom of plant',
      'Defoliation from bottom up'
    ],
    causes: ['Fungus Septoria lycopersici', 'Wet foliage', 'Splashing rain or overhead watering', 'Old plant debris'],
    prevention: [
      'Water at base of plants, avoid wetting foliage',
      'Mulch to prevent soil splash',
      'Remove and destroy infected lower leaves',
      'Rotate crops - 3 year rotation',
      'Space plants for good air circulation',
      'Remove tomato debris in fall'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Leaves',
          description: 'Prune bottom leaves',
          instructions: 'Remove yellowing and spotted lower leaves. Dispose in trash, not compost.',
          frequency: 'Weekly',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Preventive spray',
          instructions: 'Spray on foliage. Best used before disease appears or at first sign.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Baking Soda Spray',
          description: 'Homemade fungicide',
          instructions: 'Mix 1 tbsp baking soda + 1 tsp dish soap + 1 gallon water.',
          frequency: 'Every 7 days',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Broad-spectrum fungicide',
          instructions: 'Spray at first sign of disease.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'disease-verticillium-wilt',
    name: 'Verticillium Wilt',
    type: 'disease',
    affectedPlants: ['tomato', 'potato', 'eggplant', 'pepper', 'strawberry', 'raspberry'],
    symptoms: [
      'Yellowing and wilting of lower leaves on one side of plant',
      'V-shaped yellow lesions on leaves',
      'Brown discoloration in vascular tissue when stem cut',
      'Plant may wilt during day and recover at night (early stages)',
      'Eventually entire plant wilts and dies'
    ],
    causes: ['Soil-borne fungus Verticillium', 'Can persist in soil for years', 'Spread by contaminated equipment'],
    prevention: [
      'Plant resistant varieties (look for "V" on tomato labels)',
      'Rotate crops - 4 year rotation for susceptible crops',
      'Remove and destroy infected plants immediately',
      'Solarize soil in summer',
      'Improve soil drainage',
      'Sterilize tools between gardens'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure once infected',
          instructions: 'Pull entire plant immediately. Do not compost. Do not plant susceptible crops in that spot for 4 years.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Soil Solarization',
          description: 'Reduce fungus in soil',
          instructions: 'Cover soil with clear plastic for 4-6 weeks in summer.',
          frequency: 'Once per year',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Fungicides do not cure verticillium wilt',
          instructions: 'Focus on prevention and resistant varieties.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-fusarium-wilt',
    name: 'Fusarium Wilt',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'cucumber', 'melon', 'watermelon', 'bean'],
    symptoms: [
      'Yellowing and wilting starting with lower leaves',
      'Wilting progresses up the plant',
      'Brown discoloration in vascular tissue',
      'Plant may collapse suddenly',
      'Often affects plants on one side of garden first'
    ],
    causes: ['Soil-borne fungus Fusarium', 'Can persist in soil for years', 'Spread by contaminated soil and water'],
    prevention: [
      'Plant resistant varieties (look for "F" on tomato labels)',
      'Rotate crops - 5-6 year rotation',
      'Remove and destroy infected plants',
      'Avoid overwatering and poor drainage',
      'Use raised beds for better drainage',
      'Sterilize pots and tools'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure available',
          instructions: 'Pull and destroy entire plant. Do not plant susceptible crops in that area for 5+ years.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Fungicides ineffective',
          instructions: 'Prevention through resistant varieties is only option.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-downy-mildew',
    name: 'Downy Mildew',
    type: 'disease',
    affectedPlants: ['cucumber', 'squash', 'pumpkin', 'melon', 'basil', 'lettuce', 'spinach', 'onion'],
    symptoms: [
      'Yellow angular spots on upper leaf surfaces',
      'Fuzzy gray, purple, or brown growth on undersides of leaves',
      'Leaves curl and die',
      'Spreads rapidly in cool, wet weather',
      'Different from powdery mildew (which grows on upper surface)'
    ],
    causes: ['Water molds (oomycetes)', 'Cool, wet weather', 'High humidity', 'Poor air circulation'],
    prevention: [
      'Water at base of plants, avoid wetting foliage',
      'Space plants for good air circulation',
      'Plant resistant varieties',
      'Remove and destroy infected leaves',
      'Avoid overhead irrigation',
      'Rotate crops'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Leaves',
          description: 'Reduce spread',
          instructions: 'Remove and destroy infected leaves. Do not compost.',
          frequency: 'As soon as noticed',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Protectant fungicide',
          instructions: 'Spray on foliage before disease appears or at first sign.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Protectant fungicide',
          instructions: 'Spray on foliage.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest on cucumbers.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'fall']
  },

  {
    id: 'disease-anthracnose',
    name: 'Anthracnose',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'bean', 'cucumber', 'squash', 'melon', 'watermelon'],
    symptoms: [
      'Sunken, water-soaked spots on fruit',
      'Spots turn dark and may have salmon-colored spore masses',
      'Leaf spots with yellow halos',
      'Stem cankers causing wilting',
      'Fruit rot during wet weather'
    ],
    causes: ['Fungus Colletotrichum', 'Wet weather', 'Splashing rain', 'Overhead watering'],
    prevention: [
      'Water at base of plants',
      'Mulch to prevent soil splash',
      'Rotate crops - 3 year rotation',
      'Remove and destroy infected fruit and leaves',
      'Space plants for air circulation',
      'Avoid working in wet garden'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Material',
          description: 'Critical for control',
          instructions: 'Remove and destroy all infected fruit, leaves, and stems. Do not compost.',
          frequency: 'Immediately and ongoing',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Preventive spray',
          instructions: 'Spray on plants before disease appears or at first sign.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Protectant fungicide',
          instructions: 'Spray on foliage and fruit.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-damping-off',
    name: 'Damping Off',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'cucumber', 'squash', 'bean', 'lettuce', 'cabbage', 'broccoli'],
    symptoms: [
      'Seeds rot before germinating',
      'Seedlings collapse at soil line',
      'Stem at soil line becomes thin and water-soaked',
      'Seedlings fall over and die',
      'Affects young seedlings only'
    ],
    causes: ['Soil fungi (Pythium, Rhizoctonia, Fusarium)', 'Cold, wet soil', 'Overwatering', 'Poor drainage', 'Contaminated seed starting mix'],
    prevention: [
      'Use sterile seed starting mix',
      'Avoid overwatering',
      'Provide good air circulation',
      'Use clean containers',
      'Water with chamomile tea (natural fungicide)',
      'Plant seeds at proper depth',
      'Keep soil temperature warm (70-75°F)'
    ],
    treatments: {
      natural: [
        {
          name: 'Cinnamon Powder',
          description: 'Natural fungicide for soil surface',
          instructions: 'Sprinkle cinnamon on soil surface around seedlings.',
          frequency: 'Once at planting, reapply if mold appears',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Chamomile Tea Spray',
          description: 'Mild fungicide',
          instructions: 'Brew strong chamomile tea, cool, and spray on soil surface.',
          frequency: 'Every few days',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Prevention is key',
          instructions: 'Focus on prevention with sterile mix and proper watering.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'disease-leaf-miner',
    name: 'Leaf Miner',
    type: 'pest',
    affectedPlants: ['spinach', 'chard', 'beet', 'tomato', 'pepper', 'bean', 'cabbage', 'broccoli'],
    symptoms: [
      'Winding, white or yellow trails (mines) inside leaves',
      'Serpentine patterns on leaf surface',
      'Blotchy mines on leaves',
      'Small black specks (frass) inside mines',
      'Tiny larvae visible inside mines'
    ],
    causes: ['Fly larvae that feed between leaf surfaces', 'Adult flies lay eggs on leaves'],
    prevention: [
      'Use floating row covers to exclude adult flies',
      'Remove and destroy infested leaves',
      'Remove weeds in spinach and beet family',
      'Plant resistant varieties',
      'Avoid over-fertilizing with nitrogen'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infested Leaves',
          description: 'Primary control method',
          instructions: 'Pick off leaves with mines and destroy. Do not compost.',
          frequency: 'Weekly inspection',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Row Covers',
          description: 'Prevent egg-laying',
          instructions: 'Cover plants with floating row cover to exclude adult flies.',
          frequency: 'Keep covered until harvest',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Parasitic Wasps',
          description: 'Natural predators',
          instructions: 'Attract with flowers. Tiny wasps parasitize leaf miner larvae.',
          frequency: 'Ongoing',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Systemic insecticide',
          instructions: 'Spray on foliage. Absorbed by plant and kills larvae feeding inside.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees when wet. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-borers',
    name: 'Stem Borers (Various)',
    type: 'pest',
    affectedPlants: ['corn', 'squash', 'zucchini', 'pumpkin', 'pepper', 'tomato', 'bean'],
    symptoms: [
      'Holes in stems with sawdust-like frass',
      'Sudden wilting of plants',
      'Stems become mushy or hollow',
      'Larvae visible inside stems when cut open',
      'Plant collapse despite adequate water'
    ],
    causes: ['Moths or beetles lay eggs on stems', 'Larvae bore into stems and feed inside', 'Warm weather'],
    prevention: [
      'Wrap stem bases with aluminum foil or nylon stockings',
      'Use floating row covers until flowering',
      'Remove and destroy infested plants',
      'Till soil in fall to kill pupae',
      'Plant resistant varieties'
    ],
    treatments: {
      natural: [
        {
          name: 'Stem Wrapping',
          description: 'Prevents egg-laying on stem base',
          instructions: 'Wrap aluminum foil or nylon stocking around stem base from soil line to 2 inches up.',
          frequency: 'Apply when plants have 3-4 leaves',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Surgical Removal',
          description: 'Cut out borer if detected early',
          instructions: 'Slit stem lengthwise at entry hole, remove larva, mound soil over wound to encourage rooting.',
          frequency: 'As soon as wilting noticed',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Permethrin',
          description: 'Stem base spray',
          instructions: 'Spray lower stems every 5-7 days starting when vines begin to run.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest. Toxic to bees and fish.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-earwigs',
    name: 'Earwigs',
    type: 'pest',
    affectedPlants: ['lettuce', 'spinach', 'kale', 'strawberry', 'flower', 'seedling'],
    symptoms: [
      'Irregular holes in leaves and flowers',
      'Silvery slime trails (similar to slugs)',
      'Damage worst at night',
      'Pincher bugs found in damaged plants',
      'Young seedlings may be completely eaten'
    ],
    causes: ['Moist conditions', 'Mulch and debris providing hiding places', 'Nocturnal feeders'],
    prevention: [
      'Remove hiding places - boards, debris, dense weeds',
      'Use coarse mulch instead of fine mulch',
      'Keep garden free of excess moisture',
      'Set up earwig traps (rolled newspaper or cardboard tubes)'
    ],
    treatments: {
      natural: [
        {
          name: 'Oil Traps',
          description: 'Attract and trap earwigs',
          instructions: 'Fill shallow containers with vegetable oil and soy sauce. Bury so rim is at soil level.',
          frequency: 'Empty and refill every 2-3 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Newspaper Rolls',
          description: 'Hiding place trap',
          instructions: 'Roll damp newspaper and place in garden. Earwigs hide inside during day. Shake into soapy water.',
          frequency: 'Check daily',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Kills earwigs',
          instructions: 'Dust around plant bases and hiding spots.',
          frequency: 'After rain or watering',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Controls earwigs',
          instructions: 'Apply dust or spray around plants and hiding areas.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-grasshopper',
    name: 'Grasshoppers',
    type: 'pest',
    affectedPlants: ['corn', 'bean', 'lettuce', 'spinach', 'kale', 'tomato', 'pepper', 'flower'],
    symptoms: [
      'Large irregular holes in leaves',
      'Leaves chewed from edges inward',
      'Flowers and fruit damaged',
      'Stems may be chewed through',
      'Damage worse in dry years'
    ],
    causes: ['Hot, dry weather', 'Weedy areas nearby', 'Overwintered eggs in soil'],
    prevention: [
      'Keep garden weed-free (weeds are food source)',
      'Use floating row covers on vulnerable plants',
      'Encourage natural predators (birds, praying mantis)',
      'Till soil in fall to expose eggs',
      'Plant a border of tall grass to trap grasshoppers'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Physical barrier',
          instructions: 'Cover plants with floating row cover.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Garlic Spray',
          description: 'Repellent',
          instructions: 'Blend garlic with water, strain, add soap, spray on plants.',
          frequency: 'Every 3-5 days',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Nolo Bait',
          description: 'Biological control with Nosema locustae',
          instructions: 'Spread bait around garden. Grasshoppers eat it and become infected.',
          frequency: 'Apply when grasshoppers are small',
          effectiveness: 'medium',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Kills grasshoppers',
          instructions: 'Dust or spray on plants.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-stink-bug',
    name: 'Stink Bugs (Brown Marmorated)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'peach', 'apple', 'corn', 'bean', 'squash'],
    symptoms: [
      'Shield-shaped bugs (1/2 to 3/4 inch) on plants',
      'Corky or discolored spots on fruit',
      'Misshapen or deformed fruit',
      'Seeds may be destroyed inside fruit',
      'Nymphs: smaller, rounder, colored red/black/white'
    ],
    causes: ['Invasive pest from Asia', 'Overwintering in buildings and debris', 'Wide host range'],
    prevention: [
      'Seal cracks in home to prevent overwintering',
      'Remove weeds and overwintering sites',
      'Use row covers on small plantings',
      'Plant trap crops (sunflower, millet)',
      'Attract native predators with flowers'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Drop into soapy water',
          instructions: 'Pick adults and nymphs and drop into bucket of soapy water. Wear gloves - they stink!',
          frequency: 'Daily',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Row Covers',
          description: 'Exclude bugs',
          instructions: 'Cover plants with floating row cover.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin',
          description: 'Kills on contact',
          instructions: 'Spray directly on bugs.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'pest-aphid-lion',
    name: 'Ants (Farming Aphids)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'rose', 'fruit tree', 'bean', 'cabbage'],
    symptoms: [
      'Ants crawling up and down plant stems',
      'High aphid populations (ants protect them)',
      'Sticky honeydew on leaves',
      'Sooty mold on honeydew',
      'Ants visible moving aphids to new growth'
    ],
    causes: ['Ants "farm" aphids for honeydew', 'Ants protect aphids from predators', 'Sweet substances attract ants'],
    prevention: [
      'Apply sticky barrier around plant stems or tree trunks',
      'Control aphids to remove ant food source',
      'Remove ant nests near garden',
      'Keep area free of sugary spills or overripe fruit'
    ],
    treatments: {
      natural: [
        {
          name: 'Sticky Barrier',
          description: 'Prevents ants from climbing',
          instructions: 'Apply sticky tape or Tanglefoot around stem/trunk. Check and replace as needed.',
          frequency: 'Replace when dirty or full',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Kills ants',
          instructions: 'Dust around base of plants and ant trails.',
          frequency: 'After rain',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Boric Acid Bait',
          description: 'Ant bait stations',
          instructions: 'Place commercial boric acid ant baits near ant trails (not in garden).',
          frequency: 'Replace as needed',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Ant Bait Stations',
          description: 'Commercial ant baits',
          instructions: 'Place around perimeter of garden, not in garden itself.',
          frequency: 'Replace monthly',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Keep away from children and pets.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-cabbage-maggot',
    name: 'Cabbage Maggot',
    type: 'pest',
    affectedPlants: ['cabbage', 'broccoli', 'cauliflower', 'kale', 'brussels sprout', 'turnip', 'radish'],
    symptoms: [
      'Plants wilt and may die',
      'Roots have tunnels filled with small white maggots',
      'Stunted growth and yellowing',
      'Seedlings fail to establish',
      'Adult flies lay eggs at base of stems'
    ],
    causes: ['Small white flies lay eggs at plant base', 'Larvae tunnel into roots', 'Cool, wet spring weather'],
    prevention: [
      'Use floating row covers immediately after planting',
      'Plant transplants instead of direct seeding',
      'Apply collars around stem base (paper, cardboard, or fabric)',
      'Delay planting until soil warms',
      'Rotate crops - 3 year rotation'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Exclude egg-laying flies',
          instructions: 'Cover plants immediately after planting. Secure edges well.',
          frequency: 'Keep covered until harvest',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Stem Collars',
          description: 'Prevents egg-laying at base',
          instructions: 'Place 4-inch square of cardboard, paper, or fabric around stem, pressed into soil.',
          frequency: 'At planting',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Effective Chemical Treatment',
          description: 'Prevention is key',
          instructions: 'Focus on row covers and collars.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'pest-onion-maggot',
    name: 'Onion Maggot',
    type: 'pest',
    affectedPlants: ['onion', 'garlic', 'shallot', 'leek', 'chive'],
    symptoms: [
      'Seedlings wilt and collapse',
      'Bulbs have tunnels filled with white maggots',
      'Plants stunted or yellowing',
      'Soft rot may follow maggot damage',
      'Flies lay eggs at base of plants'
    ],
    causes: ['Small gray flies lay eggs at plant base', 'Larvae tunnel into bulbs', 'Cool, wet spring weather'],
    prevention: [
      'Use floating row covers',
      'Plant sets instead of seeds when possible',
      'Apply collars around stem base',
      'Rotate crops - 3 year rotation',
      'Avoid excessive organic matter (attracts flies)'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Exclude flies',
          instructions: 'Cover onion plantings immediately.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Stem Collars',
          description: 'Barrier method',
          instructions: 'Place collar around each plant base.',
          frequency: 'At planting',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Prevention only',
          instructions: 'Use row covers and collars.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'pest-birds',
    name: 'Birds (Damage)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'berry', 'cherry', 'blueberry', 'strawberry', 'corn', 'sunflower'],
    symptoms: [
      'Fruit has peck marks or is partially eaten',
      'Seeds eaten from sunflower heads',
      'Corn kernels eaten',
      'Damage concentrated on ripe fruit',
      'Bird droppings on plants'
    ],
    causes: ['Birds feeding on ripe fruit and seeds', 'Lack of natural predators', 'Easy access to food'],
    prevention: [
      'Use bird netting over fruit trees and berry bushes',
      'Use scare tactics (scarecrows, reflective tape, fake predators)',
      'Pick fruit just before fully ripe',
      'Provide alternative food source (bird feeders away from garden)',
      'Use cages around tomato plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Bird Netting',
          description: 'Physical barrier',
          instructions: 'Cover plants or fruit trees with bird netting. Secure well to prevent birds from getting trapped inside.',
          frequency: 'Install before fruit ripens',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Reflective Tape/Scare Devices',
          description: 'Visual deterrents',
          instructions: 'Hang reflective tape, old CDs, or use scarecrows. Move frequently so birds don\'t get used to them.',
          frequency: 'Move every few days',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Fake Predators',
          description: 'Owl or hawk decoys',
          instructions: 'Place fake owl or hawk near garden. Move every few days.',
          frequency: 'Move regularly',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use physical barriers',
          instructions: 'Birds are protected - use netting and deterrents only.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-rabbits',
    name: 'Rabbits',
    type: 'pest',
    affectedPlants: ['lettuce', 'spinach', 'kale', 'carrot', 'pea', 'bean', 'strawberry', 'flower', 'seedling'],
    symptoms: [
      'Clean cut stems at 45-degree angle',
      'Young plants completely eaten',
      'Leaves and stems clipped off',
      'Rabbit droppings (small round pellets) nearby',
      'Damage usually at night or early morning'
    ],
    causes: ['Rabbits seeking food', 'Lack of natural predators', 'Accessible garden without barriers'],
    prevention: [
      'Install rabbit-proof fence (chicken wire, 2-3 feet high, buried 6 inches)',
      'Use hardware cloth cages around individual plants',
      'Remove brush piles and hiding places',
      'Plant rabbit-resistant plants around perimeter',
      'Use blood meal as repellent (reapply after rain)'
    ],
    treatments: {
      natural: [
        {
          name: 'Fencing',
          description: 'Most effective solution',
          instructions: 'Install 2-3 foot high chicken wire fence, bury bottom 6 inches or bend outward to prevent digging.',
          frequency: 'One-time installation',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Plant Cages',
          description: 'Protect individual plants',
          instructions: 'Surround plants with hardware cloth cylinders.',
          frequency: 'At planting',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Repellents',
          description: 'Blood meal or commercial repellents',
          instructions: 'Sprinkle blood meal or spray commercial rabbit repellent around plants. Reapply after rain.',
          frequency: 'Every 1-2 weeks or after rain',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use fencing',
          instructions: 'Physical barriers are most effective.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-deer',
    name: 'Deer',
    type: 'pest',
    affectedPlants: ['bean', 'pea', 'lettuce', 'spinach', 'kale', 'tomato', 'pepper', 'fruit tree', 'berry', 'flower'],
    symptoms: [
      'Plants eaten down to ground',
      'Ragged, torn edges on remaining leaves (deer lack upper front teeth)',
      'Hoof prints in soil',
      'Deer droppings (pellets) nearby',
      'Damage usually at dawn or dusk'
    ],
    causes: ['Deer seeking food', 'Habitat encroachment', 'Lack of natural predators'],
    prevention: [
      'Install 8-foot tall deer fence (only reliable solution)',
      'Use deer-resistant plants',
      'Apply commercial deer repellents (must reapply frequently)',
      'Use motion-activated sprinklers or lights',
      'Plant strong-smelling herbs as border (lavender, rosemary, sage)'
    ],
    treatments: {
      natural: [
        {
          name: 'Deer Fencing',
          description: 'Only reliable solution',
          instructions: 'Install 8-foot tall fence, or two 4-foot fences 4 feet apart (deer won\'t jump both).',
          frequency: 'One-time installation',
          effectiveness: 'high',
          cost: '$$$'
        },
        {
          name: 'Motion-Activated Sprinklers',
          description: 'Startles deer',
          instructions: 'Place sprinklers around garden perimeter.',
          frequency: 'Keep charged/connected',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Deer Repellents',
          description: 'Spray or granular',
          instructions: 'Apply commercial deer repellent. Must reapply every 2-4 weeks and after rain.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use fencing',
          instructions: 'Physical barriers required for reliable control.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer', 'fall', 'winter']
  }
]

// Additional diseases
export const moreDiseases: PestDisease[] = [
  {
    id: 'disease-cedar-apple-rust',
    name: 'Cedar-Apple Rust',
    type: 'disease',
    affectedPlants: ['apple', 'crabapple', 'pear', 'hawthorn', 'cedar', 'juniper'],
    symptoms: [
      'Yellow-orange spots on leaves in spring',
      'Spots have tiny black dots in center',
      'Orange gelatinous horns form on spots in wet weather',
      'Fruit may have blemishes',
      'Cedar hosts have brown galls with orange horns in spring'
    ],
    causes: ['Fungus Gymnosporangium', 'Requires two hosts: apple and cedar/juniper', 'Wet spring weather'],
    prevention: [
      'Remove cedar/juniper trees within 1-2 miles if possible',
      'Plant resistant apple varieties (Liberty, Freedom, Enterprise)',
      'Remove galls from cedar trees before they produce horns',
      'Apply fungicide preventively in spring',
      'Rake and destroy fallen leaves in fall'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Alternate Hosts',
          description: 'Remove cedar/juniper if possible',
          instructions: 'Remove cedar trees within 1-2 miles of apple trees, or remove galls from cedars.',
          frequency: 'Inspect cedars in late winter',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Rake Leaves',
          description: 'Remove fallen infected leaves',
          instructions: 'Rake and destroy all fallen apple leaves in autumn.',
          frequency: 'Every fall',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Myclobutanil',
          description: 'Fungicide for rust diseases',
          instructions: 'Spray when buds begin to open, repeat every 7-10 days until summer.',
          frequency: 'Every 7-10 days in spring',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow label for pre-harvest interval.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring']
  },

  {
    id: 'disease-peach-leaf-curl',
    name: 'Peach Leaf Curl',
    type: 'disease',
    affectedPlants: ['peach', 'nectarine', 'apricot', 'almond'],
    symptoms: [
      'Leaves become thickened, curled, and distorted',
      'Leaves turn red or purple then yellow',
      'Leaves may drop prematurely',
      'Fruit may have blemishes or drop',
      'New shoots may be stunted'
    ],
    causes: ['Fungus Taphrina deformans', 'Infects during cool, wet spring weather', 'Spores overwinter on bark and buds'],
    prevention: [
      'Apply fungicide during dormancy (late winter before buds swell)',
      'Plant resistant varieties (Frost, Indian Free, Muir)',
      'Keep trees healthy with proper fertilization',
      'Rake and destroy fallen leaves',
      'Avoid overhead irrigation'
    ],
    treatments: {
      natural: [
        {
          name: 'Dormant Spray',
          description: 'Copper or lime sulfur in late winter',
          instructions: 'Apply copper fungicide or lime sulfur when tree is dormant, before buds swell.',
          frequency: 'Once in late winter',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Remove Infected Leaves',
          description: 'Reduce spore production',
          instructions: 'Pick off and destroy curled leaves in spring.',
          frequency: 'As noticed',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Copper Fungicide (Dormant)',
          description: 'Apply during dormancy',
          instructions: 'Spray entire tree after leaf drop in fall or before buds swell in spring.',
          frequency: 'Once during dormancy',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Must be applied during dormancy only.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring']
  },

  {
    id: 'disease-fire-blight',
    name: 'Fire Blight',
    type: 'disease',
    affectedPlants: ['apple', 'pear', 'crabapple', 'hawthorn', 'quince', 'mountain ash'],
    symptoms: [
      'Shoot tips turn black and curl (shepherd\'s crook)',
      'Blossoms turn black and wilt',
      'Twigs and branches look scorched',
      'Bark may have sunken, discolored cankers',
      'Bacterial ooze may be present in wet weather'
    ],
    causes: ['Bacterium Erwinia amylovora', 'Spread by bees, rain, and pruning tools', 'Warm, humid spring weather'],
    prevention: [
      'Plant resistant varieties',
      'Avoid excessive nitrogen fertilizer (promotes succulent growth)',
      'Avoid overhead irrigation',
      'Prune during dry weather in winter',
      'Sterilize pruning tools between cuts with 10% bleach'
    ],
    treatments: {
      natural: [
        {
          name: 'Prune Infected Branches',
          description: 'Cut 8-12 inches below visible infection',
          instructions: 'Prune during dry weather. Cut well below infected area. Sterilize tools between cuts.',
          frequency: 'As soon as noticed',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Avoid Overfertilizing',
          description: 'Prevents succulent growth',
          instructions: 'Use balanced fertilizer, avoid high nitrogen which promotes susceptible new growth.',
          frequency: 'Ongoing',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Streptomycin',
          description: 'Antibiotic for fire blight',
          instructions: 'Spray during bloom if disease was present previous year. Not for home garden use in all areas.',
          frequency: 'During bloom',
          effectiveness: 'medium',
          cost: '$$$',
          safetyNotes: 'Restricted use in some areas. Follow label exactly.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'disease-black-knot',
    name: 'Black Knot',
    type: 'disease',
    affectedPlants: ['plum', 'prune', 'cherry', 'apricot'],
    symptoms: [
      'Hard, black, swollen growths (knots) on branches',
      'Knots start as greenish-brown and turn black',
      'Knots can be 1-6 inches long',
      'Infected branches may be stunted or die',
      'Knots produce spores in spring'
    ],
    causes: ['Fungus Apiosporina morbosa', 'Spores spread by wind and rain', 'Infects new growth in spring'],
    prevention: [
      'Plant resistant varieties (President, Stanley plum)',
      'Prune out knots during dormancy',
      'Remove wild plum and cherry trees nearby',
      'Cut branches 4-6 inches below knot',
      'Sterilize pruning tools'
    ],
    treatments: {
      natural: [
        {
          name: 'Prune Out Knots',
          description: 'Remove during dormancy',
          instructions: 'Cut branches 4-6 inches below knot during winter. Sterilize tools. Burn or dispose of prunings.',
          frequency: 'Every winter',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Remove Wild Hosts',
          description: 'Eliminate nearby wild plums/cherries',
          instructions: 'Remove wild plum and cherry trees within 500 feet if possible.',
          frequency: 'One-time',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Pruning is only control',
          instructions: 'Fungicides not effective for established infections.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'disease-brown-rot',
    name: 'Brown Rot',
    type: 'disease',
    affectedPlants: ['peach', 'nectarine', 'plum', 'cherry', 'apricot'],
    symptoms: [
      'Fruit develops brown spots that expand rapidly',
      'Gray-brown spore masses on fruit (fuzzy tufts)',
      'Entire fruit rots in 1-2 days',
      'Twigs may develop cankers with gummy exudate',
      'Blossoms turn brown and wilt (blossom blight)'
    ],
    causes: ['Fungus Monilinia', 'Spread by rain, wind, and insects', 'Warm, humid weather during bloom and harvest'],
    prevention: [
      'Remove and destroy all mummified fruit and infected twigs',
      'Prune for good air circulation',
      'Avoid over-fertilizing',
      'Apply fungicide during bloom and pre-harvest',
      'Harvest fruit before it becomes overripe'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Mummified Fruit',
          description: 'Critical for control',
          instructions: 'Pick up and destroy all fallen and mummified fruit. Prune out infected twigs.',
          frequency: 'Weekly during season, thorough cleanup in fall',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Prune for Airflow',
          description: 'Reduces humidity',
          instructions: 'Open up tree canopy for better air circulation.',
          frequency: 'Annual pruning',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Captan or Chlorothalonil',
          description: 'Fungicide for brown rot',
          instructions: 'Spray during bloom and 2-3 weeks before harvest.',
          frequency: 'Every 7-10 days during susceptible periods',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow pre-harvest interval on label.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-scab',
    name: 'Apple/Pear Scab',
    type: 'disease',
    affectedPlants: ['apple', 'crabapple', 'pear', 'quince'],
    symptoms: [
      'Olive-green to black spots on leaves',
      'Spots may be velvety in texture',
      'Severely infected leaves turn yellow and drop',
      'Fruit has dark, scabby, cracked lesions',
      'Fruit may be deformed'
    ],
    causes: ['Fungus Venturia inaequalis (apple) or V. pyrina (pear)', 'Spread by rain and wind', 'Cool, wet spring weather'],
    prevention: [
      'Plant resistant varieties (Liberty, Freedom, Enterprise apples)',
      'Rake and destroy fallen leaves in autumn',
      'Prune for good air circulation',
      'Apply fungicide starting at green tip stage',
      'Avoid overhead irrigation'
    ],
    treatments: {
      natural: [
        {
          name: 'Rake Leaves',
          description: 'Remove overwintering fungus',
          instructions: 'Rake and destroy all fallen leaves in autumn. This is critical for control.',
          frequency: 'Every fall',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Suppresses mild infections',
          instructions: 'Spray on foliage.',
          frequency: 'Every 7 days',
          effectiveness: 'low',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Myclobutanil or Captan',
          description: 'Fungicide for scab',
          instructions: 'Start spraying at green tip stage, continue every 7-10 days until summer.',
          frequency: 'Every 7-10 days in spring',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow pre-harvest interval.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring']
  },

  {
    id: 'disease-bacterial-canker',
    name: 'Bacterial Canker',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'stone fruit', 'citrus'],
    symptoms: [
      'Sunken, dark cankers on stems and branches',
      'Cankers may ooze bacteria in wet weather',
      'Leaves may have dark spots with yellow halos',
      'Fruit spots are raised and scabby',
      'Branches may die back'
    ],
    causes: ['Bacteria (various species)', 'Enters through wounds or natural openings', 'Spread by rain, tools, and insects'],
    prevention: [
      'Avoid overhead irrigation',
      'Sterilize pruning tools between cuts',
      'Plant resistant varieties',
      'Avoid working in wet garden',
      'Remove and destroy infected plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Prune Out Cankers',
          description: 'Cut well below infection',
          instructions: 'Prune 4-6 inches below canker during dry weather. Sterilize tools.',
          frequency: 'As noticed',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Copper Spray',
          description: 'Bactericide',
          instructions: 'Apply copper fungicide/bactericide.',
          frequency: 'Every 7-10 days',
          effectiveness: 'low',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Copper Fungicide',
          description: 'Helps prevent spread',
          instructions: 'Spray on plants.',
          frequency: 'Every 7-10 days',
          effectiveness: 'low',
          cost: '$$',
          safetyNotes: 'Copper accumulates in soil - use sparingly.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-clubroot',
    name: 'Clubroot',
    type: 'disease',
    affectedPlants: ['cabbage', 'broccoli', 'cauliflower', 'kale', 'brussels sprout', 'turnip', 'radish', 'mustard'],
    symptoms: [
      'Stunted, yellowing, wilting plants',
      'Roots become swollen and distorted (clubs)',
      'Roots may rot in wet soil',
      'Plants may pull easily from soil',
      'Affects entire brassica family'
    ],
    causes: ['Soil-borne fungus Plasmodiophora brassicae', 'Can persist in soil for 10+ years', 'Spread by contaminated soil, water, and tools'],
    prevention: [
      'Rotate crops - 7 year rotation minimum',
      'Raise soil pH to 7.0-7.5 with lime',
      'Use raised beds with imported soil',
      'Plant resistant varieties when available',
      'Sterilize tools and boots',
      'Drain wet areas'
    ],
    treatments: {
      natural: [
        {
          name: 'Lime Soil',
          description: 'Raise pH to inhibit fungus',
          instructions: 'Add garden lime to raise soil pH to 7.0-7.5. Test soil first.',
          frequency: 'Annually',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Remove Infected Plants',
          description: 'Do not compost',
          instructions: 'Pull and destroy entire plant including roots. Do not plant brassicas in that spot for 7+ years.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Soil treatment difficult',
          instructions: 'Focus on prevention and liming.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['year-round']
  },

  {
    id: 'disease-white-mold',
    name: 'White Mold (Sclerotinia)',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'bean', 'lettuce', 'cabbage', 'carrot', 'sunflower'],
    symptoms: [
      'White fluffy mold on stems, leaves, or fruit',
      'Hard black resting bodies (sclerotia) form in mold',
      'Stems become water-soaked and girdled',
      'Plant wilts and dies',
      'Infected bean pods have white cottony growth'
    ],
    causes: ['Fungus Sclerotinia sclerotiorum', 'Thrives in cool, wet conditions', 'Sclerotia survive in soil for years'],
    prevention: [
      'Rotate crops - 3-4 year rotation',
      'Avoid overwatering and dense plantings',
      'Remove and destroy infected plants',
      'Mulch to prevent soil splash',
      'Space plants for air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'Critical for control',
          instructions: 'Pull and destroy entire plant. Do not compost. Remove surrounding soil if possible.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Improve Drainage',
          description: 'Fungus likes wet conditions',
          instructions: 'Improve soil drainage. Avoid overwatering.',
          frequency: 'Ongoing',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Effective Home Treatment',
          description: 'Prevention is key',
          instructions: 'Commercial fungicides available but expensive. Focus on prevention.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$$$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'fall']
  },

  {
    id: 'disease-alternaria',
    name: 'Alternaria Leaf Spot',
    type: 'disease',
    affectedPlants: ['tomato', 'potato', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'melon'],
    symptoms: [
      'Dark brown to black spots on leaves',
      'Spots may have concentric rings (target pattern)',
      'Yellowing around spots',
      'Spots can merge and kill leaves',
      'Fruit may develop dark, sunken lesions'
    ],
    causes: ['Fungus Alternaria', 'Spread by wind and rain', 'Favored by warm, wet weather'],
    prevention: [
      'Rotate crops - 2-3 year rotation',
      'Water at base of plants',
      'Space plants for air circulation',
      'Remove and destroy infected leaves',
      'Mulch to prevent soil splash'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Leaves',
          description: 'Reduce spread',
          instructions: 'Remove and destroy infected lower leaves.',
          frequency: 'Weekly',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Protectant spray',
          instructions: 'Spray on foliage.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Fungicide',
          instructions: 'Spray at first sign of disease.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'disease-cucumber-mosaic-virus',
    name: 'Cucumber Mosaic Virus',
    type: 'disease',
    affectedPlants: ['cucumber', 'squash', 'melon', 'pumpkin', 'tomato', 'pepper', 'lettuce', 'spinach'],
    symptoms: [
      'Mosaic pattern of light and dark green on leaves',
      'Leaves may be distorted, curled, or stunted',
      'Plants are stunted and bushy',
      'Fruit may be misshapen and discolored',
      'No cure once infected'
    ],
    causes: ['Virus spread by aphids', 'Can also spread through seeds and tools', 'Wide host range'],
    prevention: [
      'Plant resistant varieties',
      'Control aphids (they spread the virus)',
      'Remove and destroy infected plants immediately',
      'Weed control (many weeds host the virus)',
      'Wash hands and tools after handling infected plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure available',
          instructions: 'Pull and destroy entire plant immediately. Do not compost.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Control Aphids',
          description: 'Prevent spread',
          instructions: 'Control aphids to prevent virus spread to other plants.',
          frequency: 'Ongoing',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Viruses cannot be cured',
          instructions: 'Focus on prevention and aphid control.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-tobacco-mosaic-virus',
    name: 'Tobacco Mosaic Virus',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'eggplant', 'tobacco'],
    symptoms: [
      'Mottled light and dark green leaf pattern',
      'Leaves may be curled or distorted',
      'Stunted plant growth',
      'Fruit may have yellow streaks or blotches',
      'No cure once infected'
    ],
    causes: ['Virus spread by contact', 'Can survive in tobacco products', 'Spread by hands and tools'],
    prevention: [
      'Plant resistant varieties (TMV resistant)',
      'Wash hands after smoking before handling plants',
      'Sterilize tools with 10% bleach',
      'Remove and destroy infected plants',
      'Do not use tobacco products in garden'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure',
          instructions: 'Pull and destroy plant. Wash hands and sterilize tools.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Sterilize Tools',
          description: 'Prevent spread',
          instructions: 'Soak tools in 10% bleach solution for 5 minutes.',
          frequency: 'After contact with infected plants',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Cure',
          description: 'Prevention only',
          instructions: 'No chemical treatment for viruses.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-iron-deficiency',
    name: 'Iron Deficiency (Chlorosis)',
    type: 'deficiency',
    affectedPlants: ['blueberry', 'azalea', 'rhododendron', 'gardenia', 'citrus', 'tomato', 'strawberry'],
    symptoms: [
      'Yellow leaves with green veins (interveinal chlorosis)',
      'Newest leaves affected first',
      'Leaves may turn white in severe cases',
      'Plant is stunted',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['High soil pH (alkaline soil)', 'Excess phosphorus', 'Poor drainage', 'Root damage'],
    prevention: [
      'Test soil pH and adjust for plant needs',
      'For acid-loving plants, maintain pH 4.5-5.5',
      'Add sulfur to lower pH if needed',
      'Use chelated iron fertilizer',
      'Avoid over-fertilizing with phosphorus'
    ],
    treatments: {
      natural: [
        {
          name: 'Chelated Iron Foliar Spray',
          description: 'Quick fix for current leaves',
          instructions: 'Mix chelated iron according to label and spray on foliage.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Soil Sulfur',
          description: 'Lower soil pH long-term',
          instructions: 'Apply elemental sulfur to soil to lower pH. Takes months to work.',
          frequency: 'Once per year',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Iron Sulfate',
          description: 'Soil drench',
          instructions: 'Dissolve iron sulfate in water and apply to soil.',
          frequency: 'Monthly during growing season',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Iron Chelate (EDDHA)',
          description: 'Most effective iron supplement',
          instructions: 'Apply to soil or as foliar spray.',
          frequency: 'Monthly',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-nitrogen-deficiency',
    name: 'Nitrogen Deficiency',
    type: 'deficiency',
    affectedPlants: ['tomato', 'pepper', 'corn', 'lettuce', 'spinach', 'kale', 'broccoli', 'cabbage'],
    symptoms: [
      'Older leaves turn pale green to yellow',
      'Yellowing starts at leaf tips and moves inward',
      'Stunted growth',
      'Lower leaves may drop',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['Insufficient nitrogen in soil', 'Excess carbon (sawdust, wood chips) tying up nitrogen', 'Leaching from heavy rain', 'Poor soil'],
    prevention: [
      'Add compost regularly',
      'Use nitrogen-fixing cover crops (clover, beans)',
      'Apply balanced fertilizer',
      'Avoid excess wood-based mulch without added nitrogen'
    ],
    treatments: {
      natural: [
        {
          name: 'Compost or Manure',
          description: 'Slow-release nitrogen',
          instructions: 'Side-dress plants with composted manure or finished compost.',
          frequency: 'Every 4-6 weeks',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Fish Emulsion',
          description: 'Quick-acting organic nitrogen',
          instructions: 'Mix fish emulsion with water and apply to soil or as foliar feed.',
          frequency: 'Every 2-3 weeks',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Blood Meal',
          description: 'High nitrogen organic fertilizer',
          instructions: 'Side-dress plants with blood meal. Water in well.',
          frequency: 'Every 4-6 weeks',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Urea or Ammonium Nitrate',
          description: 'Fast-acting nitrogen',
          instructions: 'Apply according to label. Water in well.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-mealybug',
    name: 'Mealybugs',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'citrus', 'grape', 'orchid', 'succulent', 'houseplant'],
    symptoms: [
      'White cottony masses in leaf axils and stems',
      'Sticky honeydew on leaves',
      'Sooty mold growing on honeydew',
      'Stunted plant growth',
      'Yellowing and wilting of leaves'
    ],
    causes: ['Small soft-bodied insects covered in white wax', 'Spread by contact and ants', 'Indoor and greenhouse pest'],
    prevention: [
      'Inspect new plants before bringing indoors',
      'Keep plants healthy and not over-fertilized',
      'Control ants (they farm mealybugs)',
      'Isolate infected plants immediately',
      'Maintain good air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Rubbing Alcohol',
          description: 'Kills mealybugs on contact',
          instructions: 'Dab cotton swab in 70% rubbing alcohol and touch each mealybug.',
          frequency: 'Every few days until gone',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills mealybugs',
          instructions: 'Spray thoroughly, getting into crevices where mealybugs hide.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Neem Oil',
          description: 'Kills and repels',
          instructions: 'Spray all plant surfaces.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Systemic Insecticide',
          description: 'Imidacloprid or similar',
          instructions: 'Apply to soil. Plant absorbs and kills feeding insects.',
          frequency: 'Every 6-8 weeks',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Not for edible plants. Toxic to bees.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['year-round']
  },

  {
    id: 'pest-scale',
    name: 'Scale Insects',
    type: 'pest',
    affectedPlants: ['citrus', 'olive', 'rose', 'fruit tree', 'ornamental', 'houseplant'],
    symptoms: [
      'Small brown, black, or white bumps on stems and leaves',
      'Sticky honeydew on leaves',
      'Sooty mold on honeydew',
      'Yellowing leaves',
      'Stunted growth'
    ],
    causes: ['Immobile insects with hard protective covering', 'Spread by wind and contact', 'Ants protect and farm them'],
    prevention: [
      'Inspect new plants before bringing home',
      'Control ants',
      'Keep plants healthy',
      'Prune out heavily infested branches',
      'Maintain good air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Scrape Off',
          description: 'Physical removal',
          instructions: 'Use fingernail or soft brush to scrape scales off stems and leaves.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Horticultural Oil',
          description: 'Suffocates scales',
          instructions: 'Spray dormant oil in winter or summer oil during growing season.',
          frequency: 'Apply once in dormant season or as needed',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Insecticidal Soap',
          description: 'Kills young scales',
          instructions: 'Spray thoroughly. Most effective on crawler stage.',
          frequency: 'Every 7 days',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Systemic Insecticide',
          description: 'Imidacloprid',
          instructions: 'Apply to soil.',
          frequency: 'Every 6-8 weeks',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Not for edible plants.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['year-round']
  },

  {
    id: 'pest-fruit-fly',
    name: 'Fruit Flies',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'melon', 'squash', 'berry', 'grape'],
    symptoms: [
      'Small flies around ripe or overripe fruit',
      'Tiny maggots in fruit',
      'Fruit becomes soft and rots',
      'Eggs laid in cracks or wounds in fruit',
      'Adult flies attracted to fermenting fruit'
    ],
    causes: ['Flies attracted to ripening fruit', 'Overripe or damaged fruit', 'Compost piles near garden'],
    prevention: [
      'Harvest fruit before fully ripe',
      'Pick up and dispose of fallen fruit daily',
      'Keep compost pile away from garden',
      'Use row covers on small fruits',
      'Cover fruit with paper bags'
    ],
    treatments: {
      natural: [
        {
          name: 'Apple Cider Vinegar Trap',
          description: 'Attracts and drowns flies',
          instructions: 'Fill container with ACV and drop of dish soap. Cover with plastic wrap with holes.',
          frequency: 'Replace weekly',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Remove Overripe Fruit',
          description: 'Eliminates breeding sites',
          instructions: 'Pick up all fallen fruit and harvest before overripe.',
          frequency: 'Daily during fruiting',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Sanitation is key',
          instructions: 'Focus on removing breeding sites.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'low',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'pest-rodents',
    name: 'Rodents (Mice, Rats, Voles)',
    type: 'pest',
    affectedPlants: ['seed', 'seedling', 'root vegetable', 'tuber', 'bulb', 'fruit', 'stem'],
    symptoms: [
      'Seeds dug up and eaten',
      'Seedlings cut off at soil line',
      'Underground vegetables have teeth marks',
      'Burrows and tunnels in garden',
      'Plants wilt from root damage'
    ],
    causes: ['Rodents seeking food', 'Shelter in garden debris', 'Available water source'],
    prevention: [
      'Use hardware cloth cages around seedlings',
      'Plant seeds in rodent-proof containers until sprouted',
      'Keep garden clean of debris and hiding places',
      'Use snap traps or live traps',
      'Remove food sources (bird seed, pet food)'
    ],
    treatments: {
      natural: [
        {
          name: 'Snap Traps',
          description: 'Quick kill',
          instructions: 'Set snap traps along rodent runways. Use peanut butter as bait.',
          frequency: 'Check daily',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Hardware Cloth Baskets',
          description: 'Protect bulbs and roots',
          instructions: 'Plant bulbs and root crops in hardware cloth baskets buried in soil.',
          frequency: 'At planting',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Castor Oil Repellent',
          description: 'Repels moles and voles',
          instructions: 'Apply castor oil granules or spray to soil.',
          frequency: 'Monthly',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Rodent Bait',
          description: 'Poison bait stations',
          instructions: 'Place bait stations around garden perimeter (not in garden).',
          frequency: 'Check monthly',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Keep away from children, pets, and wildlife.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'fall', 'winter']
  },

  {
    id: 'pest-mole-cricket',
    name: 'Mole Crickets',
    type: 'pest',
    affectedPlants: ['turf', 'grass', 'seedling', 'root', 'strawberry'],
    symptoms: [
      'Tunneling damage just below soil surface',
      'Plants uprooted or damaged at roots',
      'Mounded tunnels in lawn or garden',
      'Brown patches in turf',
      'Nocturnal chirping sounds'
    ],
    causes: ['Large crickets that burrow underground', 'Feed on roots and tubers', 'Active at night'],
    prevention: [
      'Improve drainage (they like moist soil)',
      'Use beneficial nematodes in spring',
      'Keep lawn healthy to outcompete damage',
      'Apply soapy water flush to bring to surface'
    ],
    treatments: {
      natural: [
        {
          name: 'Soapy Water Flush',
          description: 'Brings crickets to surface',
          instructions: 'Mix 2 tbsp dish soap per gallon water. Pour over suspected area. Crickets will surface in 5-10 minutes.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Beneficial Nematodes',
          description: 'Parasitic to mole crickets',
          instructions: 'Apply Steinernema scapterisci nematodes to soil in spring.',
          frequency: 'Spring application',
          effectiveness: 'high',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Insecticide Granules',
          description: 'For severe infestations',
          instructions: 'Apply granules to soil and water in.',
          frequency: 'Spring application',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow label carefully.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-gall-midge',
    name: 'Gall Midges',
    type: 'pest',
    affectedPlants: ['rose', 'juniper', 'oak', 'maple', 'wheat', 'rice'],
    symptoms: [
      'Abnormal swellings (galls) on leaves, stems, or flowers',
      'Galls may be round, spiky, or irregular',
      'Distorted growth',
      'Reduced plant vigor in severe cases',
      'Tiny maggots inside galls'
    ],
    causes: ['Tiny fly larvae that induce plant to form galls', 'Species-specific to host plants'],
    prevention: [
      'Plant resistant varieties',
      'Prune out galls when noticed',
      'Keep plants healthy',
      'Rake and destroy fallen leaves with galls'
    ],
    treatments: {
      natural: [
        {
          name: 'Prune Out Galls',
          description: 'Remove before adults emerge',
          instructions: 'Cut off galls and destroy. Do not compost.',
          frequency: 'As noticed',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Systemic Insecticide',
          description: 'For severe cases',
          instructions: 'Apply in early spring before galls form.',
          frequency: 'Spring application',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Not for edible plants.'
        }
      ]
    },
    severity: 'low',
    seasonality: ['spring']
  },

  {
    id: 'pest-sawfly',
    name: 'Sawflies',
    type: 'pest',
    affectedPlants: ['rose', 'birch', 'pine', 'spruce', 'currant', 'gooseberry'],
    symptoms: [
      'Caterpillar-like larvae feeding on leaves',
      'Leaves skeletonized or completely eaten',
      'Often feed in groups',
      'Larvae have more pairs of prolegs than butterfly caterpillars',
      'Adults are wasp-like flies'
    ],
    causes: ['Sawfly larvae hatch from eggs on leaves', 'Feed gregariously', 'Multiple generations per year'],
    prevention: [
      'Inspect plants regularly',
      'Hand pick egg clusters and larvae',
      'Encourage birds and beneficial insects',
      'Use row covers on small plantings'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Effective for small infestations',
          instructions: 'Pick off larvae and drop in soapy water. Look for egg clusters on leaves.',
          frequency: 'Daily during infestation',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Kills larvae',
          instructions: 'Spray on leaves where larvae feed.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad',
          description: 'Effective on sawflies',
          instructions: 'Spray on foliage.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees when wet.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-tent-caterpillar',
    name: 'Tent Caterpillars',
    type: 'pest',
    affectedPlants: ['cherry', 'plum', 'apple', 'pear', 'peach', 'oak', 'birch'],
    symptoms: [
      'Silken tents in branch crotches',
      'Caterpillars clustered in tents',
      'Defoliation of branches',
      'Black caterpillars with white stripe and blue spots',
      'Tents expand as colony grows'
    ],
    causes: ['Moths lay egg masses on branches in summer', 'Caterpillars hatch and build communal nests', 'Feed in groups'],
    prevention: [
      'Remove egg masses from branches in winter (look like foam collars)',
      'Prune out tents when small',
      'Encourage birds',
      'Keep trees healthy'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Tents',
          description: 'Prune or knock down',
          instructions: 'Prune out small tents and destroy. For large tents, poke with stick to tear open and expose caterpillars to birds.',
          frequency: 'As soon as noticed',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Kills caterpillars',
          instructions: 'Spray on caterpillars and tents.',
          frequency: 'Once',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin',
          description: 'Kills caterpillars',
          instructions: 'Spray on tents and caterpillars.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring']
  },

  {
    id: 'pest-bagworm',
    name: 'Bagworms',
    type: 'pest',
    affectedPlants: ['evergreen', 'arborvitae', 'juniper', 'pine', 'spruce', 'deciduous tree'],
    symptoms: [
      'Small cone-shaped bags hanging from branches',
      'Bags made of plant material and silk',
      'Defoliation of branches',
      'Bags contain caterpillar that feeds on leaves',
      'Bags enlarge as caterpillar grows'
    ],
    causes: ['Female moth is wingless and never leaves bag', 'Larvae spin bags and feed from inside', 'Bags contain 500-1000 eggs'],
    prevention: [
      'Hand pick bags in winter before eggs hatch',
      'Inspect evergreens regularly',
      'Remove bags before June (when eggs hatch)',
      'Keep trees healthy'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Pick Bags',
          description: 'Most effective control',
          instructions: 'Pick off all bags in winter or early spring before eggs hatch. Destroy bags - do not compost.',
          frequency: 'Winter/early spring',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Kills young larvae',
          instructions: 'Spray in June when larvae are small and emerging from bags.',
          frequency: 'Once in June',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Spinosad or Bifenthrin',
          description: 'For heavy infestations',
          instructions: 'Spray in June when larvae are active.',
          frequency: 'Once or twice in June',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Toxic to bees.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'pest-webworm',
    name: 'Webworms (Fall Webworm)',
    type: 'pest',
    affectedPlants: ['walnut', 'hickory', 'pecan', 'cherry', 'plum', 'birch', 'willow', 'mulberry'],
    symptoms: [
      'Large unsightly webs at ends of branches',
      'Caterpillars feed inside web',
      'Leaves skeletonized within web',
      'Webs expand to cover more foliage',
      'Yellow, hairy caterpillars with black spots'
    ],
    causes: ['Moths lay eggs on undersides of leaves in summer', 'Caterpillars build communal webs', 'Feed inside protective web'],
    prevention: [
      'Prune out webs when small',
      'Encourage birds',
      'Keep trees healthy',
      'Usually cosmetic damage only'
    ],
    treatments: {
      natural: [
        {
          name: 'Prune Out Webs',
          description: 'Remove small webs',
          instructions: 'Prune out webs and destroy. For large webs, poke to open and let birds feed.',
          frequency: 'As noticed',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Bt Spray',
          description: 'Kills caterpillars',
          instructions: 'Spray into webs.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Usually Not Needed',
          description: 'Cosmetic damage only',
          instructions: 'Webs are unsightly but rarely harm healthy trees.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'low',
    seasonality: ['summer', 'fall']
  }
]

// Additional diseases
export const moreDiseases2: PestDisease[] = [
  {
    id: 'disease-crown-gall',
    name: 'Crown Gall',
    type: 'disease',
    affectedPlants: ['rose', 'fruit tree', 'grape', 'berry', 'tomato', 'pepper', 'stone fruit'],
    symptoms: [
      'Rough, tumor-like growths on roots or at soil line',
      'Galls are woody and irregular in shape',
      'Stunted growth and yellowing',
      'Plant may decline over several years',
      'Galls can be pea-sized to several inches'
    ],
    causes: ['Bacterium Agrobacterium tumefaciens', 'Enters through wounds', 'Soil-borne and persistent'],
    prevention: [
      'Buy certified disease-free plants',
      'Avoid wounding plants',
      'Sterilize pruning tools',
      'Plant in well-drained soil',
      'Remove and destroy infected plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure available',
          instructions: 'Dig up and destroy entire plant including roots. Do not replant susceptible species in that spot.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Prune Galls',
          description: 'For valuable plants only',
          instructions: 'Cut away galls with sterile tools, cutting well into healthy tissue. Sterilize tools after.',
          frequency: 'As noticed',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Prevention only',
          instructions: 'No chemical cure for crown gall.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['year-round']
  },

  {
    id: 'disease-gummy-stem-blight',
    name: 'Gummy Stem Blight',
    type: 'disease',
    affectedPlants: ['cucumber', 'squash', 'pumpkin', 'melon', 'watermelon'],
    symptoms: [
      'Water-soaked spots on leaves with dark borders',
      'Stem cankers with gummy exudate',
      'Vines wilt and die',
      'Fruit develop dark, sunken spots',
      'Pink spore masses in wet conditions'
    ],
    causes: ['Fungus Didymella bryoniae', 'Spread by rain and wind', 'Favored by warm, wet weather'],
    prevention: [
      'Use disease-free seed',
      'Rotate crops - 3 year rotation',
      'Avoid overhead irrigation',
      'Space plants for air circulation',
      'Remove and destroy infected plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'Critical for control',
          instructions: 'Remove and destroy entire infected plant.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Copper Fungicide',
          description: 'Protectant spray',
          instructions: 'Spray preventively or at first sign.',
          frequency: 'Every 7-10 days',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Chlorothalonil',
          description: 'Fungicide',
          instructions: 'Spray at first sign.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-pythium-root-rot',
    name: 'Pythium Root Rot',
    type: 'disease',
    affectedPlants: ['seedling', 'tomato', 'pepper', 'cucumber', 'squash', 'flower', 'houseplant'],
    symptoms: [
      'Seedlings collapse at soil line (damping off)',
      'Roots are brown and mushy',
      'Plants wilt despite adequate water',
      'Stunted growth',
      'Root cortex slips off easily'
    ],
    causes: ['Water mold Pythium', 'Overwatering', 'Poor drainage', 'Cool, wet soil conditions'],
    prevention: [
      'Use well-drained soil',
      'Avoid overwatering',
      'Use sterile potting mix',
      'Water from bottom when possible',
      'Space plants for air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Improve Drainage',
          description: 'Most important step',
          instructions: 'Repot in well-drained soil. Allow soil to dry between waterings.',
          frequency: 'Ongoing',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Home Chemical Treatment',
          description: 'Cultural control only',
          instructions: 'Focus on drainage and watering practices.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'disease-rust',
    name: 'Rust Diseases',
    type: 'disease',
    affectedPlants: ['bean', 'rose', 'wheat', 'snapdragon', 'hollyhock', 'iris', 'daylily', 'asparagus'],
    symptoms: [
      'Orange, yellow, or brown pustules on undersides of leaves',
      'Corresponding yellow spots on upper leaf surface',
      'Pustules release powdery spores when touched',
      'Leaves may yellow and drop',
      'Severely infected plants are stunted'
    ],
    causes: ['Various rust fungi', 'Many require two host plants', 'Spread by wind', 'Favored by wet conditions'],
    prevention: [
      'Plant resistant varieties',
      'Water at base of plants',
      'Space plants for air circulation',
      'Remove and destroy infected leaves',
      'Rake and destroy fallen leaves in fall'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Leaves',
          description: 'Reduce spread',
          instructions: 'Pick off and destroy infected leaves.',
          frequency: 'Weekly',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Baking Soda Spray',
          description: 'Suppresses rust',
          instructions: 'Mix 1 tbsp baking soda + 1 tsp oil + 1 gallon water. Spray on leaves.',
          frequency: 'Every 7 days',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Myclobutanil or Propiconazole',
          description: 'Fungicide for rust',
          instructions: 'Spray at first sign.',
          frequency: 'Every 7-14 days',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow label.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-leaf-curl',
    name: 'Leaf Curl (Plum)',
    type: 'disease',
    affectedPlants: ['peach', 'nectarine', 'plum', 'apricot', 'almond'],
    symptoms: [
      'Leaves become thickened, curled, and distorted',
      'Leaves turn red or purple',
      'White powdery spores on upper leaf surface',
      'Leaves may drop',
      'Fruit may be affected in severe cases'
    ],
    causes: ['Fungus Taphrina deformans', 'Same as peach leaf curl', 'Infects during cool, wet spring weather'],
    prevention: [
      'Apply fungicide during dormancy',
      'Plant resistant varieties',
      'Rake and destroy fallen leaves',
      'Prune for air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Dormant Spray',
          description: 'Copper or lime sulfur',
          instructions: 'Apply during dormancy before buds swell.',
          frequency: 'Once in late winter',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Copper Fungicide',
          description: 'Dormant application',
          instructions: 'Spray during dormancy.',
          frequency: 'Once',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Apply only during dormancy.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring']
  },

  {
    id: 'disease-phytophthora',
    name: 'Phytophthora Blight',
    type: 'disease',
    affectedPlants: ['pepper', 'tomato', 'eggplant', 'cucumber', 'squash', 'pumpkin', 'potato'],
    symptoms: [
      'Dark, water-soaked lesions on stems',
      'Rapid wilting and death of plants',
      'White fungal growth on stems in wet conditions',
      'Fruit develop dark, firm rot',
      'Roots may be rotted'
    ],
    causes: ['Water mold Phytophthora capsici', 'Thrives in wet, poorly drained soil', 'Can persist in soil for years'],
    prevention: [
      'Improve soil drainage',
      'Use raised beds',
      'Rotate crops - 4 year rotation',
      'Avoid overhead irrigation',
      'Remove and destroy infected plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Improve Drainage',
          description: 'Critical for control',
          instructions: 'Install drainage or use raised beds.',
          frequency: 'One-time',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Remove Infected Plants',
          description: 'Prevent spread',
          instructions: 'Dig up and destroy entire plant and surrounding soil if possible.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Effective Home Treatment',
          description: 'Prevention and drainage',
          instructions: 'Focus on cultural controls.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-southern-blight',
    name: 'Southern Blight',
    type: 'disease',
    affectedPlants: ['tomato', 'pepper', 'bean', 'squash', 'strawberry', 'flower'],
    symptoms: [
      'Sudden wilting of plants',
      'White fungal growth at soil line',
      'Small tan to brown mustard-seed sized fungal bodies (sclerotia)',
      'Stem becomes girdled and rots',
      'Plant collapses'
    ],
    causes: ['Fungus Sclerotium rolfsii', 'Thrives in hot, humid weather', 'Sclerotia survive in soil for years'],
    prevention: [
      'Rotate crops - 4 year rotation',
      'Remove and destroy infected plants immediately',
      'Mulch to keep soil cool',
      'Solarize soil in summer',
      'Space plants for air circulation'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'Remove soil around plant too',
          instructions: 'Dig up plant and surrounding soil. Destroy - do not compost.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Soil Solarization',
          description: 'Kills sclerotia',
          instructions: 'Cover soil with clear plastic for 4-6 weeks in summer.',
          frequency: 'Summer treatment',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Home Treatment',
          description: 'Cultural control',
          instructions: 'Prevention and sanitation are key.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'disease-greasy-spot',
    name: 'Greasy Spot (Citrus)',
    type: 'disease',
    affectedPlants: ['citrus', 'lemon', 'lime', 'orange', 'grapefruit'],
    symptoms: [
      'Yellow-brown blister-like spots on leaves',
      'Spots become greasy-looking',
      'Black fungal growth on undersides of leaves',
      'Severe defoliation in bad cases',
      'Fruit may have blemishes'
    ],
    causes: ['Fungus Mycosphaerella citri', 'Spread by wind and rain', 'Favored by warm, humid conditions'],
    prevention: [
      'Rake and destroy fallen leaves',
      'Prune for air circulation',
      'Apply fungicide in summer and fall',
      'Keep trees healthy'
    ],
    treatments: {
      natural: [
        {
          name: 'Rake Leaves',
          description: 'Remove overwintering fungus',
          instructions: 'Rake and destroy all fallen citrus leaves.',
          frequency: 'Year-round',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Copper Fungicide',
          description: 'For citrus',
          instructions: 'Spray in summer and fall.',
          frequency: 'Every 3-4 weeks',
          effectiveness: 'high',
          cost: '$$',
          safetyNotes: 'Follow label for citrus.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'disease-mosaic-virus',
    name: 'Rose Mosaic Virus',
    type: 'disease',
    affectedPlants: ['rose'],
    symptoms: [
      'Yellow wavy lines or rings on leaves',
      'Mottled light and dark green pattern',
      'Leaves may be distorted',
      'Plant growth may be stunted',
      'Flowers may be fewer or off-color'
    ],
    causes: ['Virus', 'Spread through infected grafting material', 'Not spread by insects'],
    prevention: [
      'Buy certified virus-free plants',
      'Remove and destroy infected plants',
      'Do not propagate from infected plants',
      'Control nematodes (may spread virus)'
    ],
    treatments: {
      natural: [
        {
          name: 'Remove Infected Plants',
          description: 'No cure',
          instructions: 'Dig up and destroy infected rose. Do not replant rose in same spot.',
          frequency: 'Immediately',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Viruses cannot be cured',
          instructions: 'Prevention through certified stock.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['year-round']
  },

  {
    id: 'disease-magnesium-deficiency',
    name: 'Magnesium Deficiency',
    type: 'deficiency',
    affectedPlants: ['tomato', 'pepper', 'potato', 'rose', 'citrus', 'palm', 'apple'],
    symptoms: [
      'Yellowing between leaf veins on older leaves',
      'Leaves may show orange, red, or purple tints',
      'Veins stay green',
      'Leaf edges may curl upward',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['Insufficient magnesium in soil', 'High potassium levels (compete with magnesium uptake)', 'Acidic soils', 'Leaching from heavy rain'],
    prevention: [
      'Add dolomitic lime to raise pH and add magnesium',
      'Use balanced fertilizer',
      'Add compost regularly',
      'Test soil and adjust accordingly'
    ],
    treatments: {
      natural: [
        {
          name: 'Epsom Salt (Magnesium Sulfate)',
          description: 'Quick fix',
          instructions: 'Mix 1-2 tbsp Epsom salt per gallon water. Spray on foliage or apply to soil.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Dolomitic Lime',
          description: 'Long-term solution',
          instructions: 'Apply to soil to raise pH and add magnesium.',
          frequency: 'Annually',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Magnesium Chelate',
          description: 'Foliar feed',
          instructions: 'Spray on foliage.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'low',
    seasonality: ['summer']
  },

  {
    id: 'disease-calcium-deficiency',
    name: 'Calcium Deficiency',
    type: 'deficiency',
    affectedPlants: ['tomato', 'pepper', 'squash', 'melon', 'apple', 'celery', 'lettuce'],
    symptoms: [
      'Blossom end rot on tomatoes and peppers',
      'Tip burn on lettuce and cabbage',
      'Cavity in apple centers (bitter pit)',
      'New leaves distorted or stunted',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['Insufficient calcium uptake', 'Inconsistent watering', 'High salt levels', 'Root damage', 'Rapid plant growth'],
    prevention: [
      'Maintain consistent soil moisture',
      'Add calcium to soil before planting (lime, gypsum)',
      'Avoid over-fertilizing with nitrogen',
      'Mulch to regulate soil moisture',
      'Foliar calcium sprays during fruit development'
    ],
    treatments: {
      natural: [
        {
          name: 'Calcium Foliar Spray',
          description: 'Quick fix',
          instructions: 'Mix calcium chloride or calcium nitrate (2-3 tbsp per gallon) and spray on leaves and fruit.',
          frequency: 'Every 5-7 days during fruiting',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Gypsum or Lime',
          description: 'Soil amendment',
          instructions: 'Add gypsum (does not affect pH) or lime (raises pH) to soil.',
          frequency: 'At planting or annually',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Consistent Watering',
          description: 'Most important',
          instructions: 'Water regularly and mulch to maintain even soil moisture.',
          frequency: 'Ongoing',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Calcium Nitrate',
          description: 'Foliar or soil',
          instructions: 'Apply as foliar spray or soil drench.',
          frequency: 'Every 7 days',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'disease-phosphorus-deficiency',
    name: 'Phosphorus Deficiency',
    type: 'deficiency',
    affectedPlants: ['tomato', 'pepper', 'corn', 'bean', 'flower', 'fruit tree'],
    symptoms: [
      'Dark green or bluish-green leaves',
      'Leaves may develop purple coloration',
      'Stunted growth',
      'Delayed maturity',
      'Poor fruit or flower production'
    ],
    causes: ['Insufficient phosphorus in soil', 'Cold soil (phosphorus unavailable)', 'Soil pH too high or low', 'Excess iron or zinc'],
    prevention: [
      'Add bone meal or rock phosphate at planting',
      'Use balanced fertilizer',
      'Maintain soil pH 6.0-7.0',
      'Add compost to improve phosphorus availability'
    ],
    treatments: {
      natural: [
        {
          name: 'Bone Meal',
          description: 'Organic phosphorus source',
          instructions: 'Side-dress plants with bone meal. Work into soil and water in.',
          frequency: 'At planting or mid-season',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Rock Phosphate',
          description: 'Slow-release phosphorus',
          instructions: 'Work into soil. Slow to become available but long-lasting.',
          frequency: 'Annually',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Fish Bone Meal',
          description: 'Fast-acting organic phosphorus',
          instructions: 'Apply to soil around plants.',
          frequency: 'Every 4-6 weeks',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Superphosphate',
          description: 'Fast-acting phosphorus',
          instructions: 'Apply according to label.',
          frequency: 'At planting or as needed',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-potassium-deficiency',
    name: 'Potassium Deficiency',
    type: 'deficiency',
    affectedPlants: ['tomato', 'potato', 'pepper', 'fruit tree', 'berry', 'palm'],
    symptoms: [
      'Yellowing and browning of leaf edges on older leaves',
      'Leaves may curl downward',
      'Weak stems',
      'Poor fruit quality and color',
      'Increased disease susceptibility'
    ],
    causes: ['Insufficient potassium in soil', 'Sandy soils that leach nutrients', 'Excess calcium or magnesium', 'Drought stress'],
    prevention: [
      'Add compost regularly',
      'Use balanced fertilizer',
      'Mulch to conserve moisture',
      'Add greensand or kelp meal'
    ],
    treatments: {
      natural: [
        {
          name: 'Greensand',
          description: 'Slow-release potassium',
          instructions: 'Apply to soil. Slow to release but long-lasting.',
          frequency: 'Annually',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Kelp Meal',
          description: 'Organic potassium source',
          instructions: 'Side-dress plants or make into tea.',
          frequency: 'Every 4-6 weeks',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Wood Ash',
          description: 'Fast potassium (use sparingly)',
          instructions: 'Sprinkle small amount around plants. Raises pH.',
          frequency: 'Once per season',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Use sparingly - can raise pH too much.'
        }
      ],
      chemical: [
        {
          name: 'Sulfate of Potash',
          description: 'Fast-acting potassium',
          instructions: 'Apply to soil.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer']
  },

  {
    id: 'pest-aphid-lion',
    name: 'Ants (Farming Aphids)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'rose', 'fruit tree', 'bean', 'cabbage'],
    symptoms: [
      'Ants crawling up and down plant stems',
      'High aphid populations (ants protect them)',
      'Sticky honeydew on leaves',
      'Sooty mold on honeydew',
      'Ants visible moving aphids to new growth'
    ],
    causes: ['Ants "farm" aphids for honeydew', 'Ants protect aphids from predators', 'Sweet substances attract ants'],
    prevention: [
      'Apply sticky barrier around plant stems or tree trunks',
      'Control aphids to remove ant food source',
      'Remove ant nests near garden',
      'Keep area free of sugary spills or overripe fruit'
    ],
    treatments: {
      natural: [
        {
          name: 'Sticky Barrier',
          description: 'Prevents ants from climbing',
          instructions: 'Apply sticky tape or Tanglefoot around stem/trunk. Check and replace as needed.',
          frequency: 'Replace when dirty or full',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Kills ants',
          instructions: 'Dust around base of plants and ant trails.',
          frequency: 'After rain',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Boric Acid Bait',
          description: 'Ant bait stations',
          instructions: 'Place commercial boric acid ant baits near ant trails (not in garden).',
          frequency: 'Replace as needed',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Ant Bait Stations',
          description: 'Commercial ant baits',
          instructions: 'Place around perimeter of garden, not in garden itself.',
          frequency: 'Replace monthly',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Keep away from children and pets.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-cabbage-maggot',
    name: 'Cabbage Maggot',
    type: 'pest',
    affectedPlants: ['cabbage', 'broccoli', 'cauliflower', 'kale', 'brussels sprout', 'turnip', 'radish'],
    symptoms: [
      'Plants wilt and may die',
      'Roots have tunnels filled with small white maggots',
      'Stunted growth and yellowing',
      'Seedlings fail to establish',
      'Adult flies lay eggs at base of stems'
    ],
    causes: ['Small white flies lay eggs at plant base', 'Larvae tunnel into roots', 'Cool, wet spring weather'],
    prevention: [
      'Use floating row covers immediately after planting',
      'Plant transplants instead of direct seeding',
      'Apply collars around stem base (paper, cardboard, or fabric)',
      'Delay planting until soil warms',
      'Rotate crops - 3 year rotation'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Exclude egg-laying flies',
          instructions: 'Cover plants immediately after planting. Secure edges well.',
          frequency: 'Keep covered until harvest',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Stem Collars',
          description: 'Prevents egg-laying at base',
          instructions: 'Place 4-inch square of cardboard, paper, or fabric around stem, pressed into soil.',
          frequency: 'At planting',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Effective Chemical Treatment',
          description: 'Prevention is key',
          instructions: 'Focus on row covers and collars.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'pest-onion-maggot',
    name: 'Onion Maggot',
    type: 'pest',
    affectedPlants: ['onion', 'garlic', 'shallot', 'leek', 'chive'],
    symptoms: [
      'Seedlings wilt and collapse',
      'Bulbs have tunnels filled with white maggots',
      'Plants stunted or yellowing',
      'Soft rot may follow maggot damage',
      'Flies lay eggs at base of plants'
    ],
    causes: ['Small gray flies lay eggs at plant base', 'Larvae tunnel into bulbs', 'Cool, wet spring weather'],
    prevention: [
      'Use floating row covers',
      'Plant sets instead of seeds when possible',
      'Apply collars around stem base',
      'Rotate crops - 3 year rotation',
      'Avoid excessive organic matter (attracts flies)'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Exclude flies',
          instructions: 'Cover onion plantings immediately.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Stem Collars',
          description: 'Barrier method',
          instructions: 'Place collar around each plant base.',
          frequency: 'At planting',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Prevention only',
          instructions: 'Use row covers and collars.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring']
  },

  {
    id: 'pest-borers',
    name: 'Stem Borers (Various)',
    type: 'pest',
    affectedPlants: ['corn', 'squash', 'zucchini', 'pumpkin', 'pepper', 'tomato', 'bean'],
    symptoms: [
      'Holes in stems with sawdust-like frass',
      'Sudden wilting of plants',
      'Stems become mushy or hollow',
      'Larvae visible inside stems when cut open',
      'Plant collapse despite adequate water'
    ],
    causes: ['Moths or beetles lay eggs on stems', 'Larvae bore into stems and feed inside', 'Warm weather'],
    prevention: [
      'Wrap stem bases with aluminum foil or nylon stockings',
      'Use floating row covers until flowering',
      'Remove and destroy infested plants',
      'Till soil in fall to kill pupae',
      'Plant resistant varieties'
    ],
    treatments: {
      natural: [
        {
          name: 'Stem Wrapping',
          description: 'Prevents egg-laying on stem base',
          instructions: 'Wrap aluminum foil or nylon stocking around stem base from soil line to 2 inches up.',
          frequency: 'Apply when plants have 3-4 leaves',
          effectiveness: 'high',
          cost: '$'
        },
        {
          name: 'Surgical Removal',
          description: 'Cut out borer if detected early',
          instructions: 'Slit stem lengthwise at entry hole, remove larva, mound soil over wound to encourage rooting.',
          frequency: 'As soon as wilting noticed',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Permethrin',
          description: 'Stem base spray',
          instructions: 'Spray lower stems every 5-7 days starting when vines begin to run.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Wait 3 days before harvest. Toxic to bees and fish.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-earwigs',
    name: 'Earwigs',
    type: 'pest',
    affectedPlants: ['lettuce', 'spinach', 'kale', 'strawberry', 'flower', 'seedling'],
    symptoms: [
      'Irregular holes in leaves and flowers',
      'Silvery slime trails (similar to slugs)',
      'Damage worst at night',
      'Pincher bugs found in damaged plants',
      'Young seedlings may be completely eaten'
    ],
    causes: ['Moist conditions', 'Mulch and debris providing hiding places', 'Nocturnal feeders'],
    prevention: [
      'Remove hiding places - boards, debris, dense weeds',
      'Use coarse mulch instead of fine mulch',
      'Keep garden free of excess moisture',
      'Set up earwig traps (rolled newspaper or cardboard tubes)'
    ],
    treatments: {
      natural: [
        {
          name: 'Oil Traps',
          description: 'Attract and trap earwigs',
          instructions: 'Fill shallow containers with vegetable oil and soy sauce. Bury so rim is at soil level.',
          frequency: 'Empty and refill every 2-3 days',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Newspaper Rolls',
          description: 'Hiding place trap',
          instructions: 'Roll damp newspaper and place in garden. Earwigs hide inside during day. Shake into soapy water.',
          frequency: 'Check daily',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Diatomaceous Earth',
          description: 'Kills earwigs',
          instructions: 'Dust around plant bases and hiding spots.',
          frequency: 'After rain or watering',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Controls earwigs',
          instructions: 'Apply dust or spray around plants and hiding areas.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-grasshopper',
    name: 'Grasshoppers',
    type: 'pest',
    affectedPlants: ['corn', 'bean', 'lettuce', 'spinach', 'kale', 'tomato', 'pepper', 'flower'],
    symptoms: [
      'Large irregular holes in leaves',
      'Leaves chewed from edges inward',
      'Flowers and fruit damaged',
      'Stems may be chewed through',
      'Damage worse in dry years'
    ],
    causes: ['Hot, dry weather', 'Weedy areas nearby', 'Overwintered eggs in soil'],
    prevention: [
      'Keep garden weed-free (weeds are food source)',
      'Use floating row covers on vulnerable plants',
      'Encourage natural predators (birds, praying mantis)',
      'Till soil in fall to expose eggs',
      'Plant a border of tall grass to trap grasshoppers'
    ],
    treatments: {
      natural: [
        {
          name: 'Row Covers',
          description: 'Physical barrier',
          instructions: 'Cover plants with floating row cover.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Garlic Spray',
          description: 'Repellent',
          instructions: 'Blend garlic with water, strain, add soap, spray on plants.',
          frequency: 'Every 3-5 days',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Nolo Bait',
          description: 'Biological control with Nosema locustae',
          instructions: 'Spread bait around garden. Grasshoppers eat it and become infected.',
          frequency: 'Apply when grasshoppers are small',
          effectiveness: 'medium',
          cost: '$$$'
        }
      ],
      chemical: [
        {
          name: 'Carbaryl (Sevin)',
          description: 'Kills grasshoppers',
          instructions: 'Dust or spray on plants.',
          frequency: 'Every 7-10 days',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'high',
    seasonality: ['summer']
  },

  {
    id: 'pest-stink-bug',
    name: 'Stink Bugs (Brown Marmorated)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'peach', 'apple', 'corn', 'bean', 'squash'],
    symptoms: [
      'Shield-shaped bugs (1/2 to 3/4 inch) on plants',
      'Corky or discolored spots on fruit',
      'Misshapen or deformed fruit',
      'Seeds may be destroyed inside fruit',
      'Nymphs: smaller, rounder, colored red/black/white'
    ],
    causes: ['Invasive pest from Asia', 'Overwintering in buildings and debris', 'Wide host range'],
    prevention: [
      'Seal cracks in home to prevent overwintering',
      'Remove weeds and overwintering sites',
      'Use row covers on small plantings',
      'Plant trap crops (sunflower, millet)',
      'Attract native predators with flowers'
    ],
    treatments: {
      natural: [
        {
          name: 'Hand Picking',
          description: 'Drop into soapy water',
          instructions: 'Pick adults and nymphs and drop into bucket of soapy water. Wear gloves - they stink!',
          frequency: 'Daily',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Row Covers',
          description: 'Exclude bugs',
          instructions: 'Cover plants with floating row cover.',
          frequency: 'Keep covered',
          effectiveness: 'high',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'Pyrethrin',
          description: 'Kills on contact',
          instructions: 'Spray directly on bugs.',
          frequency: 'Every 5-7 days',
          effectiveness: 'medium',
          cost: '$$',
          safetyNotes: 'Toxic to bees. Apply in evening.'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['summer', 'fall']
  },

  {
    id: 'pest-birds',
    name: 'Birds (Damage)',
    type: 'pest',
    affectedPlants: ['tomato', 'pepper', 'berry', 'cherry', 'blueberry', 'strawberry', 'corn', 'sunflower'],
    symptoms: [
      'Fruit has peck marks or is partially eaten',
      'Seeds eaten from sunflower heads',
      'Corn kernels eaten',
      'Damage concentrated on ripe fruit',
      'Bird droppings on plants'
    ],
    causes: ['Birds feeding on ripe fruit and seeds', 'Lack of natural predators', 'Easy access to food'],
    prevention: [
      'Use bird netting over fruit trees and berry bushes',
      'Use scare tactics (scarecrows, reflective tape, fake predators)',
      'Pick fruit just before fully ripe',
      'Provide alternative food source (bird feeders away from garden)',
      'Use cages around tomato plants'
    ],
    treatments: {
      natural: [
        {
          name: 'Bird Netting',
          description: 'Physical barrier',
          instructions: 'Cover plants or fruit trees with bird netting. Secure well to prevent birds from getting trapped inside.',
          frequency: 'Install before fruit ripens',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Reflective Tape/Scare Devices',
          description: 'Visual deterrents',
          instructions: 'Hang reflective tape, old CDs, or use scarecrows. Move frequently so birds don\'t get used to them.',
          frequency: 'Move every few days',
          effectiveness: 'low',
          cost: '$'
        },
        {
          name: 'Fake Predators',
          description: 'Owl or hawk decoys',
          instructions: 'Place fake owl or hawk near garden. Move every few days.',
          frequency: 'Move regularly',
          effectiveness: 'low',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use physical barriers',
          instructions: 'Birds are protected - use netting and deterrents only.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-rabbits',
    name: 'Rabbits',
    type: 'pest',
    affectedPlants: ['lettuce', 'spinach', 'kale', 'carrot', 'pea', 'bean', 'strawberry', 'flower', 'seedling'],
    symptoms: [
      'Clean cut stems at 45-degree angle',
      'Young plants completely eaten',
      'Leaves and stems clipped off',
      'Rabbit droppings (small round pellets) nearby',
      'Damage usually at night or early morning'
    ],
    causes: ['Rabbits seeking food', 'Shelter in garden debris', 'Accessible garden without barriers'],
    prevention: [
      'Install rabbit-proof fence (chicken wire, 2-3 feet high, buried 6 inches)',
      'Use hardware cloth cages around individual plants',
      'Remove brush piles and hiding places',
      'Plant rabbit-resistant plants around perimeter',
      'Use blood meal as repellent (reapply after rain)'
    ],
    treatments: {
      natural: [
        {
          name: 'Fencing',
          description: 'Most effective solution',
          instructions: 'Install 2-3 foot high chicken wire fence, bury bottom 6 inches or bend outward to prevent digging.',
          frequency: 'One-time installation',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Plant Cages',
          description: 'Protect individual plants',
          instructions: 'Surround plants with hardware cloth cylinders.',
          frequency: 'At planting',
          effectiveness: 'high',
          cost: '$$'
        },
        {
          name: 'Repellents',
          description: 'Blood meal or commercial repellents',
          instructions: 'Sprinkle blood meal or spray commercial rabbit repellent around plants. Reapply after rain.',
          frequency: 'Every 1-2 weeks or after rain',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use fencing',
          instructions: 'Physical barriers are most effective.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer', 'fall']
  },

  {
    id: 'pest-deer',
    name: 'Deer',
    type: 'pest',
    affectedPlants: ['bean', 'pea', 'lettuce', 'spinach', 'kale', 'tomato', 'pepper', 'fruit tree', 'berry', 'flower'],
    symptoms: [
      'Plants eaten down to ground',
      'Ragged, torn edges on remaining leaves (deer lack upper front teeth)',
      'Hoof prints in soil',
      'Deer droppings (pellets) nearby',
      'Damage usually at dawn or dusk'
    ],
    causes: ['Deer seeking food', 'Habitat encroachment', 'Lack of natural predators'],
    prevention: [
      'Install 8-foot tall deer fence (only reliable solution)',
      'Use deer-resistant plants',
      'Apply commercial deer repellents (must reapply frequently)',
      'Use motion-activated sprinklers or lights',
      'Plant strong-smelling herbs as border (lavender, rosemary, sage)'
    ],
    treatments: {
      natural: [
        {
          name: 'Deer Fencing',
          description: 'Only reliable solution',
          instructions: 'Install 8-foot tall fence, or two 4-foot fences 4 feet apart (deer won\'t jump both).',
          frequency: 'One-time installation',
          effectiveness: 'high',
          cost: '$$$'
        },
        {
          name: 'Motion-Activated Sprinklers',
          description: 'Startles deer',
          instructions: 'Place sprinklers around garden perimeter.',
          frequency: 'Keep charged/connected',
          effectiveness: 'medium',
          cost: '$$'
        },
        {
          name: 'Deer Repellents',
          description: 'Spray or granular',
          instructions: 'Apply commercial deer repellent. Must reapply every 2-4 weeks and after rain.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'medium',
          cost: '$$'
        }
      ],
      chemical: [
        {
          name: 'No Chemical Treatment',
          description: 'Use fencing',
          instructions: 'Physical barriers required for reliable control.',
          frequency: 'N/A',
          effectiveness: 'low',
          cost: '$'
        }
      ]
    },
    severity: 'high',
    seasonality: ['spring', 'summer', 'fall', 'winter']
  }
]

// Additional diseases batch 3
export const moreDiseases3: PestDisease[] = [
  {
    id: 'disease-zinc-deficiency',
    name: 'Zinc Deficiency',
    type: 'deficiency',
    affectedPlants: ['citrus', 'apple', 'pear', 'peach', 'corn', 'bean', 'tomato'],
    symptoms: [
      'Small, narrow leaves',
      'Leaves show interveinal chlorosis (yellow between veins)',
      'Shortened internodes (rosetting)',
      'Little leaf disease in citrus',
      'Poor fruit set'
    ],
    causes: ['High soil pH', 'High phosphorus levels', 'Sandy soils', 'Cool wet soil'],
    prevention: [
      'Maintain proper soil pH',
      'Use balanced fertilizer',
      'Add zinc to soil before planting',
      'Foliar zinc sprays'
    ],
    treatments: {
      natural: [
        {
          name: 'Zinc Sulfate Foliar Spray',
          description: 'Quick fix',
          instructions: 'Mix zinc sulfate with water and spray on foliage.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Zinc Chelate',
          description: 'Foliar or soil application',
          instructions: 'Apply according to label.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-boron-deficiency',
    name: 'Boron Deficiency',
    type: 'deficiency',
    affectedPlants: ['apple', 'pear', 'celery', 'cauliflower', 'beet', 'turnip', 'strawberry'],
    symptoms: [
      'Thickened, curled, brittle leaves',
      'Death of growing points',
      'Hollow stems in broccoli and cauliflower',
      'Brown heart in turnips',
      'Cracked or corky fruit in apples'
    ],
    causes: ['Sandy soils', 'High pH soils', 'Drought conditions', 'Excessive liming'],
    prevention: [
      'Add boron to soil based on soil test',
      'Use balanced fertilizer with micronutrients',
      'Maintain consistent soil moisture',
      'Avoid over-liming'
    ],
    treatments: {
      natural: [
        {
          name: 'Borax Solution',
          description: 'Apply carefully - toxic in excess',
          instructions: 'Dissolve 1 tbsp borax in 1 gallon water. Apply to soil around plants.',
          frequency: 'Once per season',
          effectiveness: 'high',
          cost: '$',
          safetyNotes: 'Use sparingly - boron is toxic to plants in excess.'
        }
      ],
      chemical: [
        {
          name: 'Solubor',
          description: 'Boron fertilizer',
          instructions: 'Apply according to label.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-manganese-deficiency',
    name: 'Manganese Deficiency',
    type: 'deficiency',
    affectedPlants: ['citrus', 'blueberry', 'soybean', 'oat', 'pea', 'bean'],
    symptoms: [
      'Interveinal chlorosis on young leaves',
      'Veins remain green',
      'May show brown necrotic spots',
      'Similar to magnesium deficiency but on young leaves',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['High soil pH', 'Sandy soils', 'Excessive iron', 'Poor drainage'],
    prevention: [
      'Maintain proper soil pH for crop',
      'Add manganese to deficient soils',
      'Use acidifying fertilizer for acid-loving plants',
      'Improve drainage'
    ],
    treatments: {
      natural: [
        {
          name: 'Manganese Sulfate Foliar Spray',
          description: 'Quick fix',
          instructions: 'Mix manganese sulfate with water and spray on foliage.',
          frequency: 'Every 2-4 weeks',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Manganese Chelate',
          description: 'Soil or foliar application',
          instructions: 'Apply according to label.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'low',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-copper-deficiency',
    name: 'Copper Deficiency',
    type: 'deficiency',
    affectedPlants: ['wheat', 'oat', 'onion', 'lettuce', 'carrot', 'citrus', 'avocado'],
    symptoms: [
      'Wilting and dieback of young leaves',
      'Leaves may turn blue-green',
      'Twisted or malformed leaves',
      'Failure to flower',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['High soil pH', 'Sandy soils', 'Excessive nitrogen or phosphorus', 'Organic soils'],
    prevention: [
      'Apply copper before planting',
      'Maintain proper soil pH',
      'Use balanced fertilizer',
      'Add organic matter'
    ],
    treatments: {
      natural: [
        {
          name: 'Copper Sulfate',
          description: 'Soil or foliar application',
          instructions: 'Apply copper sulfate to soil or as foliar spray.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Copper Chelate',
          description: 'Foliar spray',
          instructions: 'Apply according to label.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$$'
        }
      ]
    },
    severity: 'medium',
    seasonality: ['spring', 'summer']
  },

  {
    id: 'disease-sulfur-deficiency',
    name: 'Sulfur Deficiency',
    type: 'deficiency',
    affectedPlants: ['cabbage', 'broccoli', 'cauliflower', 'onion', 'garlic', 'legume'],
    symptoms: [
      'Overall yellowing of young leaves',
      'Similar to nitrogen deficiency but on new growth',
      'Stunted growth',
      'Leaves may be pale green to yellow',
      'Not a disease - nutrient deficiency'
    ],
    causes: ['Sandy soils', 'Low organic matter', 'Excessive rainfall (leaching)', 'High nitrogen without sulfur'],
    prevention: [
      'Add sulfur or gypsum to soil',
      'Use sulfur-containing fertilizers',
      'Add compost regularly',
      'Avoid excessive nitrogen without sulfur'
    ],
    treatments: {
      natural: [
        {
          name: 'Elemental Sulfur',
          description: 'Lowers pH and adds sulfur',
          instructions: 'Apply to soil and work in. Takes time to become available.',
          frequency: 'Annually',
          effectiveness: 'medium',
          cost: '$'
        },
        {
          name: 'Gypsum',
          description: 'Adds sulfur without changing pH',
          instructions: 'Apply to soil.',
          frequency: 'Annually',
          effectiveness: 'medium',
          cost: '$'
        }
      ],
      chemical: [
        {
          name: 'Ammonium Sulfate',
          description: 'Fertilizer with sulfur',
          instructions: 'Apply according to label.',
          frequency: 'As needed',
          effectiveness: 'high',
          cost: '$'
        }
      ]
    },
    severity: 'low',
    seasonality: ['spring', 'summer']
  }
]

// Combine all pests and diseases
export const allPests: PestDisease[] = [...originalPests, ...additionalPests]
export const allDiseases: PestDisease[] = [...originalDiseases, ...additionalDiseases, ...moreDiseases, ...moreDiseases2, ...moreDiseases3]

// Re-export combined arrays as the main exports
export { allPests as pests, allDiseases as diseases }

// Helper functions - use combined arrays
export const getPestsByPlant = (plantId: string): PestDisease[] => {
  return allPests.filter(pest => 
    pest.affectedPlants.includes(plantId) || 
    pest.affectedPlants.some(plant => plantId.includes(plant))
  )
}

export const getDiseasesByPlant = (plantId: string): PestDisease[] => {
  return allDiseases.filter(disease => 
    disease.affectedPlants.includes(plantId) ||
    disease.affectedPlants.some(plant => plantId.includes(plant))
  )
}

export const getAllProblems = (): PestDisease[] => [...allPests, ...allDiseases]

export const getProblemById = (id: string): PestDisease | undefined => {
  return getAllProblems().find(p => p.id === id)
}

export const searchProblems = (query: string): PestDisease[] => {
  const lowerQuery = query.toLowerCase()
  return getAllProblems().filter(problem => 
    problem.name.toLowerCase().includes(lowerQuery) ||
    problem.symptoms.some(s => s.toLowerCase().includes(lowerQuery))
  )
}

export default {
  pests: allPests,
  diseases: allDiseases,
  getPestsByPlant,
  getDiseasesByPlant,
  getAllProblems,
  getProblemById,
  searchProblems
}
